import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { T, Badge, Card, Btn, Modal } from "./atoms";
import { fmtDateOnly, fmtInstant, isDateOnlyPast, localDateString } from "../../utils/dates";
import { complexityForDisplay, isQuickWinRating } from "../findingRatings";
import { countsAsAddressed, countsAsOpen, STATUS_OVERRIDE_NOTE_LIFECYCLE_OPTIONS } from "../overrideStatus";

export const StatusOverrideOptionButton = ({ option, active, onToggle }) => (
  <button type="button" onClick={onToggle}
    aria-pressed={active}
    style={{
      display:"block", width:"100%", textAlign:"left", padding:"10px 12px",
      marginTop:8, borderRadius:8,
      border:`1px solid ${active ? "rgba(168,85,247,0.55)" : T.border}`,
      background: active ? "rgba(168,85,247,0.18)" : "rgba(255,255,255,0.03)",
      cursor:"pointer", fontFamily:"inherit", color:T.text,
    }}>
    <div style={{ display:"flex", alignItems:"center", gap:8 }}>
      <span style={{
        width:16, height:16, borderRadius:"50%",
        border:`2px solid ${active ? "#DDD6FE" : T.muted}`,
        background: active ? "#DDD6FE" : "transparent",
        display:"inline-grid", placeItems:"center", flexShrink:0,
        color:"#1F2937", fontSize:11, fontWeight:800, lineHeight:1,
      }} aria-hidden="true">{active ? "✓" : ""}</span>
      <span style={{ fontSize:13, fontWeight:600 }}>{option.label}</span>
    </div>
    <div style={{ marginLeft:22, fontSize:11, color:T.muted, marginTop:2 }}>
      {option.helper}
    </div>
  </button>
);


const DISPOSITION_LABELS = {
  planned: "Planned",
  risk_accepted: "Risk Accepted",
  customer_managed_solution: "Customer Managed",
  customer_remediated: "Customer Remediated",
};

const dispositionLabel = (key) => DISPOSITION_LABELS[key] || key || "—";



const formatReviewDate = (value) => fmtDateOnly(value, { month: "short", day: "numeric" }, "");

// Monotonic id for optimistic provisional rows (reviews / session-focus items)
// that exist only until the server-created row replaces them. Namespaced so it
// never collides with a real Dataverse row id.



// Past-deadline check for revisit_by (a DATE-ONLY column): local calendar
// comparison — due-today is not stale. The previous instant comparison read
// the midnight-Z wire value as "past" from local evening onward.
const isStale = (value) => isDateOnlyPast(value);

const relativeTime = (iso) => {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (isNaN(then)) return "";
  const diffMs = Date.now() - then;
  if (diffMs < 60000) return "just now";
  const min = Math.round(diffMs / 60000);
  if (min < 60) return `${min} minute${min === 1 ? "" : "s"} ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} hour${hr === 1 ? "" : "s"} ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day} day${day === 1 ? "" : "s"} ago`;
  const mo = Math.round(day / 30);
  if (mo < 12) return `${mo} month${mo === 1 ? "" : "s"} ago`;
  const yr = Math.round(mo / 12);
  return `${yr} year${yr === 1 ? "" : "s"} ago`;
};


const PRODUCT_CAT_MAP = {
  Auth:["Auth","Passwords"], Admins:["Admins"],
  "SharePoint Online":["SharePoint","SharePoint Online"],
  "Microsoft Teams":["Teams","Microsoft Teams"],
  "Exchange Online":["Exchange","Exchange Online"],
  "Entra ID":["Entra ID","Identity"], Defender:["Defender"],
  "Power Platform":["Power Platform"], "AI Governance":["AI Governance", "Copilot"],
  "Identity & Access Mgmt":["Auth","Entra ID","Identity","Passwords"],
  "Network Security":["Defender"], "Data Protection":["SharePoint","SharePoint Online","Power Platform","Compliance"],
  "Endpoint Security":["Defender"], "Email Security":["Exchange","Exchange Online"],
  "Collaboration Security":["Teams","Microsoft Teams","AI Governance","Copilot"], "Privileged Access":["Admins"],
  "AI Governance":["AI Governance","Copilot"], "Compliance & DLP":["Compliance","SharePoint","SharePoint Online","Power Platform"],
};

const SETTINGS_DETAIL = {
  327:"Review 3rd party OAuth apps quarterly.",143:"Disable 3rd party hosted storage in Office Online.",
  918:"Allow users to report suspicious MFA activities.",358:"Fewer than 5 Global Admin accounts.",
  958:"Emergency access accounts should use FIDO2 keys.",669:"Remove synced account admin permissions.",
  409:"Configure Defender for Identity Entity Tags.",934:"Resolve Sensor health issues in Defender XDR.",
  1055:"Restrict Copilot agent access/install.",1056:"Restrict who can share agents org-wide.",
  1065:"Disable Anthropic AI models in Copilot.",1035:"Restrict subscription transfer out of directory.",
  6:"Disable Seamless SSO.",857:"Disable 'Keep me signed in'.",
  736:"Expire guest access links (30-730 days).",390:"Disable download of infected files.",
  1054:"Disable SSPR policy for administrators.",1037:"Disable Direct Send in Exchange Online.",
  807:"Review risky Workload identities.",1051:"Enable weaponizable file protection in Teams.",
  1052:"Enable malicious URL protection in Teams.",126:"Disable Microsoft Store via GPO/Intune.",
  896:"Enable geo-location in MFA push notifications.",646:"Enable Endpoint DLP Service Domains.",
  555:"Limit Power BI semantic model access.",626:"Disable Egnyte storage in Teams.",
  652:"Block macros via Office Apps Policy.",
};

// Severity \u2192 max score weight. Mirrors rubric until Gold Copy wires real
// Score Weight from Dataverse.
const SEVERITY_MAX = { critical: 4, high: 3, medium: 2, low: 1 };
const getMaxWeight = (sev) => SEVERITY_MAX[sev] ?? 1;

const MAX_QUEUE = 10;


function sevBadgeType(s) {
  const norm = (s || "").toLowerCase();
  if (norm === "critical") return "purple";
  if (norm === "high") return "red";
  if (norm === "medium") return "orange";
  if (norm === "low") return "yellow";
  if (norm === "informational" || norm === "governance") return "slate";
  return "slate";
}

// Title-case severity for badge labels. Data is lowercase (critical/high/medium/low/informational),
// but display should match Status column casing (Critical / High / Medium / Low / Informational).
function sevLabel(s) {
  const norm = (s || "").toLowerCase();
  if (norm === "informational" || norm === "governance") return "Informational";
  if (!norm) return "";
  return norm.charAt(0).toUpperCase() + norm.slice(1);
}

function statusBadge(status) {
  const map = {
    open:          { label: "! Open",                    type: "blue"   },
    implemented:   { label: "✓ Satisfied",          type: "green"  },
    partial:       { label: "◐ Partial",            type: "amber"  },
    exception:     { label: "⚠ Risk Accepted",      type: "purple" },
    pending:       { label: "⏳ Pending",            type: "orange" },
    planned:       { label: "📅 Planned",      type: "indigo" },
    managed_solution: { label: "◇ Customer Managed", type: "teal" },
    "in-progress": { label: "⏳ In Progress",        type: "yellow" },
    deferred:      { label: "Deferred",                  type: "slate"  },
    // Not Assessed (2026-07-28 denominator decision): informational gray —
    // the Check-badge treatment from the scans tab, not an alarm state.
    not_assessed:  { label: "– Not Assessed",            type: "slate"  },
  };
  return map[status] || map.open;
}

// Severity ordering for sorting (most → least severe). informational/governance
// share the lowest rank; unknown values sort last (caller uses ?? 99).
const SEV_SORT_RANK = { critical: 0, high: 1, medium: 2, low: 3, informational: 4, governance: 4 };

// Effective-status ordering for the Status column sort (open work first →
// resolved/credited last). Unknown statuses sort last (caller uses ?? 99).
const STATUS_SORT_RANK = { open: 0, partial: 1, pending: 2, planned: 3, exception: 4, managed_solution: 5, implemented: 6, not_assessed: 7 };

// A row's EFFECTIVE status key — mirrors the statusCell precedence in the recs
// table (override wins over the scored status). Returns a statusBadge key.
function effectiveStatusKey(r, settingDetails) {
  const ov = settingDetails?.[r.id]?.override;
  if (ov === "risk_accepted")             return "exception";
  if (ov === "customer_remediated")       return "pending";
  if (ov === "planned")                   return "planned";
  if (ov === "customer_managed_solution") return "managed_solution";
  return r.status;
}

function ageString(firstIdentified) {
  if (!firstIdentified) return "—"; // em-dash
  const start = new Date(firstIdentified);
  if (isNaN(start)) return "—";
  const today = new Date();
  const daysDiff = Math.max(1, Math.floor((today - start) / 86400000));
  if (daysDiff < 7) return `${daysDiff} day${daysDiff === 1 ? "" : "s"}`;
  if (daysDiff < 30) {
    const weeks = Math.floor(daysDiff / 7);
    return `${weeks} week${weeks === 1 ? "" : "s"}`;
  }
  if (daysDiff < 365) {
    const months = Math.floor(daysDiff / 30);
    return `${months} month${months === 1 ? "" : "s"}`;
  }
  const years = Math.floor(daysDiff / 365);
  const remMonths = Math.floor((daysDiff % 365) / 30);
  return remMonths === 0 ? `${years} year${years === 1 ? "" : "s"}` : `${years}y ${remMonths}mo`;
}




const Skeleton = ({ width = "100%", height = 16, borderRadius = 6, style = {} }) => (
  <div style={{
    width, height, borderRadius,
    background: "linear-gradient(90deg, rgba(255,255,255,.04) 0%, rgba(255,255,255,.10) 50%, rgba(255,255,255,.04) 100%)",
    backgroundSize: "200% 100%",
    animation: "ss365-skeleton-shimmer 1.5s ease-in-out infinite",
    ...style,
  }} />
);

// Donut placeholder — a ring shape using two concentric circles, matching the
// 160px CategoryDonut used in the score cards.


// Skeleton variants for OverviewPage sub-sections. Each mirrors the real
// component's layout so the visual transition to populated data is stable.






// Skeleton for the Next Session Focus card — sits in the right column of the
// top grid alongside the four metric cards.
const SkeletonNextSessionFocus = () => (
  <div style={{
    background: T.panel,
    border: `1px solid ${T.border}`,
    borderLeft: "3px solid rgba(83,196,109,.60)",
    borderRadius: 12,
    padding: "16px 20px",
    marginTop: 16,
  }}>
    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
      <Skeleton width={150} height={15} />
      <Skeleton width={50} height={11} />
    </div>
    <Skeleton width="60%" height={11} style={{ marginBottom:14 }} />
    {[0,1,2].map(i => (
      <div key={i} style={{ display:"flex", gap:10, marginBottom:8, alignItems:"center" }}>
        <Skeleton width={36} height={12} />
        <Skeleton width="60%" height={12} />
        <Skeleton width={60} height={18} borderRadius={999} />
        <Skeleton width={50} height={11} />
      </div>
    ))}
  </div>
);

// Skeleton table for the Recommendations page.
const SkeletonRecsTable = () => (
  <Card>
    <div style={{ display:"flex", gap:10, marginBottom:14 }}>
      <Skeleton width={120} height={28} borderRadius={8} />
      <Skeleton width={120} height={28} borderRadius={8} />
      <Skeleton width={120} height={28} borderRadius={8} />
    </div>
    {[0,1,2,3,4,5,6,7,8,9].map(i => (
      <div key={i} style={{ display:"flex", alignItems:"center", gap:14, padding:"10px 0", borderBottom:`1px solid ${T.border}` }}>
        <Skeleton width={36} height={12} />
        <Skeleton width={110} height={20} borderRadius={999} />
        <div style={{ flex:1 }}>
          <Skeleton width="70%" height={13} />
        </div>
        <Skeleton width={70} height={12} />
        <Skeleton width={70} height={20} borderRadius={999} />
        <Skeleton width={40} height={12} />
      </div>
    ))}
  </Card>
);

// Empty state shown on customer pages when the scored report fetch returns
// no data (e.g., a brand-new customer with no completed scan). Distinct from
// the skeleton, which only renders while a fetch is in-flight. The optional
// action renders as a single primary button under the message.
const EmptyState = ({ title, message, icon = "📊", actionLabel, onAction }) => (
  <div style={{
    background: T.panel,
    border: `1px solid ${T.border}`,
    borderRadius: 12,
    padding: "48px 32px",
    textAlign: "center",
    marginBottom: 16,
  }}>
    <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.5 }}>{icon}</div>
    <h3 style={{ margin: "0 0 12px", fontSize: 18, fontWeight: 700, color: T.text }}>
      {title}
    </h3>
    <p style={{ margin: 0, fontSize: 14, color: T.muted, maxWidth: 480, marginLeft: "auto", marginRight: "auto", lineHeight: 1.5 }}>
      {message}
    </p>
    {actionLabel && onAction && (
      <div style={{ marginTop: 20 }}>
        <Btn pri onClick={onAction}>{actionLabel}</Btn>
      </div>
    )}
  </div>
);

const ROSTER_INACTIVE_TITLE = "Portal access not active";
const ROSTER_INACTIVE_MESSAGE = "Your portal access for this organization isn't currently active. Please contact your Patriot team to restore access. If your access was just restored, refresh this page.";
const NO_DATA_TITLE = "No assessment data available yet";
const NO_DATA_MESSAGE = "Once your first SecureShield365 scan completes, your security posture, recommendations, and session focus will appear here. Questions? Contact your Patriot engineer.";

// Self-scan-aware no-data copy (approved 2026-08-01). The variant comes
// from classifyNoDataScanState over the customer's own engine runs,
// fetched only after the report request settles at 404. Keyed by that
// classifier's return values; anything unrecognized (including null from
// a failed runs read) falls back to the generic NO_DATA copy above.
// Deliberately silent on who reviews/publishes results — publish
// mechanics are changing (auto-publish) and this copy must survive that.
const NO_DATA_COPY = {
  no_scans: {
    icon: "🛡️",
    title: "Run your first security scan",
    message: "Your dashboard fills in after your first scan. SecureScan365 is a read-only check of your Microsoft 365 environment that you run yourself, and it takes a few minutes. Start it from your Company Profile page.",
    actionLabel: "Go to Company Profile",
  },
  scan_running: {
    icon: "⏳",
    title: "Your scan is running",
    message: "A scan of your Microsoft 365 environment is in progress — no need to start another. You can leave this page; results appear here when it finishes.",
  },
  awaiting_publish: {
    icon: "✅",
    title: "Scan complete — results on the way",
    message: "Your scan finished and the results are being processed. Nothing further is needed from you. Your security posture and recommendations will appear here shortly.",
  },
};

// Shared by the three report-backed pages (Overview, Recommendations,
// Queue) so every no-data landing tells the same story.
const NoDataEmptyState = ({ scanState, onGoToProfile }) => {
  const copy = NO_DATA_COPY[scanState];
  if (!copy) return <EmptyState title={NO_DATA_TITLE} message={NO_DATA_MESSAGE} />;
  return (
    <EmptyState
      title={copy.title}
      message={copy.message}
      icon={copy.icon}
      actionLabel={copy.actionLabel}
      onAction={copy.actionLabel ? onGoToProfile : undefined}
    />
  );
};


const ViewToggle = ({ options, value, onChange, size = "md" }) => {
  const sm = size === "sm";
  return (
    <div style={{ display:"inline-flex", background:"rgba(255,255,255,.06)", border:`1px solid ${T.border}`, borderRadius: sm ? 8 : 10, overflow:"hidden" }}>
      {options.map(o => (
        <button key={o.value} onClick={() => onChange(o.value)} style={{
          padding: sm ? "3px 10px" : "7px 14px", fontSize: sm ? 11 : 12, fontWeight:600,
          background: value === o.value ? (sm ? "rgba(68,166,255,.14)" : "rgba(68,166,255,.2)") : "transparent",
          border:"none", color: value === o.value ? T.blue : T.muted, cursor:"pointer", fontFamily:"inherit", transition:".15s",
        }}>{o.label}</button>
      ))}
    </div>
  );
};


/* ── NoteItem — one note card inside the drawer's Notes section.
   Phase 1 added read-only display; Phase 4 adds inline edit + delete. ── */
const NoteItem = ({ note, settingId, onEdit, onDelete, isPatriot: viewerIsPatriot = false }) => {
  const isPatriot = ((note.author_type || note.author || "") + "").toLowerCase() === "patriot";
  // Team-perm: a viewer can edit/delete a note only when its author_type
  // matches their team. Patriot ↔ Patriot, Customer ↔ Customer. Unknown
  // author_type means we can't prove a match → hide the affordances.
  const noteAuthorType = ((note.author_type || "") + "").toLowerCase();
  const viewerAuthorType = viewerIsPatriot ? "patriot" : "customer";
  const canEditNote = noteAuthorType === viewerAuthorType;
  const lifecycleRaw = ((note.lifecycle || "ongoing") + "").toLowerCase();
  const isShortTerm = lifecycleRaw === "short-term" || lifecycleRaw === "short_term" || lifecycleRaw === "shortterm";
  const stale = isShortTerm && note.revisit_by && isStale(note.revisit_by);
  const modified = note.modified_on || note.updated_at || note.modified_at || note.created_on || note.created_at;
  const currentLifecycleLabel = isShortTerm ? "Short-term" : "Ongoing";
  const currentRevisitDate = note.revisit_by ? String(note.revisit_by).split("T")[0] : "";

  const [editMode, setEditMode] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editBody, setEditBody] = useState(note.body || note.text || "");
  const [editLifecycle, setEditLifecycle] = useState(currentLifecycleLabel);
  const [editRevisitBy, setEditRevisitBy] = useState(currentRevisitDate);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const resetEditState = () => {
    setEditBody(note.body || note.text || "");
    setEditLifecycle(currentLifecycleLabel);
    setEditRevisitBy(currentRevisitDate);
    setError(null);
  };

  const handleSaveEdit = async () => {
    if (!editBody.trim()) return;
    const patch = {};
    const trimmed = editBody.trim();
    if (trimmed !== (note.body || note.text || "")) patch.body = trimmed;
    if (editLifecycle !== currentLifecycleLabel) patch.lifecycle = editLifecycle;
    if (editLifecycle === "Short-term") {
      // Only send revisit_by when it actually changed; null clears it.
      if (editRevisitBy && editRevisitBy !== currentRevisitDate) patch.revisit_by = editRevisitBy;
      else if (!editRevisitBy && currentRevisitDate) patch.revisit_by = null;
    }
    // Switching to Ongoing: server auto-clears revisit_by, no need to send it.
    if (Object.keys(patch).length === 0) {
      setEditMode(false);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onEdit(settingId, note.id, patch);
      setEditMode(false);
    } catch (err) {
      setError(err.message || "Failed to update note");
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await onDelete(settingId, note.id);
      // Parent unmounts this card via state update; no further work needed.
    } catch (err) {
      setError(err.message || "Failed to delete note");
      setSubmitting(false);
      setConfirmDelete(false);
    }
  };

  const authorPill = isPatriot
    ? { bg: "rgba(68,166,255,0.18)", color: "#9ecdff", border: "rgba(68,166,255,0.45)" }
    : { bg: "rgba(168,189,220,0.12)", color: T.muted, border: T.border };

  const lifecyclePill = isShortTerm
    ? { bg: "rgba(243,181,63,0.15)", color: "#ffe4a5", border: "rgba(243,181,63,0.45)" }
    : { bg: "rgba(83,196,109,0.15)", color: "#bff5c8", border: "rgba(83,196,109,0.45)" };

  const pillStyle = (p) => ({
    padding: "2px 8px", borderRadius: 999,
    background: p.bg, color: p.color, border: `1px solid ${p.border}`,
    fontWeight: 600, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.04em",
    whiteSpace: "nowrap",
  });

  const iconBtnStyle = {
    background: "transparent",
    border: "none",
    color: T.muted,
    cursor: "pointer",
    padding: "2px 6px",
    borderRadius: 6,
    fontSize: 13,
    lineHeight: 1,
  };

  return (
    <div style={{
      position: "relative",
      background: "rgba(255,255,255,0.04)",
      border: `1px solid ${T.border}`,
      borderRadius: 10,
      padding: "12px 14px",
      marginTop: 10,
    }}>
      {/* Edit/Delete affordances — top-right; hidden when the viewer's
          team doesn't match the note's author_type */}
      {canEditNote && !editMode && !confirmDelete && (
        <div style={{ position: "absolute", top: 8, right: 8, display: "flex", gap: 2 }}>
          <button
            type="button"
            title="Edit note"
            onClick={() => { resetEditState(); setEditMode(true); }}
            style={iconBtnStyle}
            onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.06)"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
          >
            ✏️
          </button>
          <button
            type="button"
            title="Delete note"
            onClick={() => setConfirmDelete(true)}
            style={iconBtnStyle}
            onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.06)"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
          >
            🗑️
          </button>
        </div>
      )}

      {/* Delete confirmation row */}
      {confirmDelete && (
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: 8, marginBottom: 10, padding: "8px 10px",
          background: "rgba(225,77,87,0.08)", border: `1px solid rgba(225,77,87,0.35)`,
          borderRadius: 8, fontSize: 12, color: T.text,
        }}>
          <span>Delete this note?</span>
          <span style={{ display: "flex", gap: 6 }}>
            <button
              type="button"
              onClick={() => { setConfirmDelete(false); setError(null); }}
              disabled={submitting}
              style={{
                background: "transparent", color: T.muted, border: `1px solid ${T.border}`,
                borderRadius: 6, padding: "4px 10px", fontSize: 11,
                cursor: submitting ? "not-allowed" : "pointer",
              }}
            >Cancel</button>
            <button
              type="button"
              onClick={handleConfirmDelete}
              disabled={submitting}
              style={{
                background: T.red, color: "#fff", border: "none",
                borderRadius: 6, padding: "4px 10px", fontSize: 11, fontWeight: 700,
                cursor: submitting ? "not-allowed" : "pointer",
                opacity: submitting ? 0.6 : 1,
              }}
            >{submitting ? "Deleting..." : "Confirm Delete"}</button>
          </span>
        </div>
      )}

      {/* Body — either edit form or rendered note */}
      {editMode ? (
        <div>
          <textarea
            value={editBody}
            onChange={e => setEditBody(e.target.value)}
            rows={4}
            style={{
              width: "100%", minHeight: 80,
              background: "rgba(0,0,0,0.25)", color: T.text,
              border: `1px solid ${T.border}`, borderRadius: 8,
              padding: "8px 10px", fontSize: 13, lineHeight: 1.55,
              fontFamily: "inherit", resize: "vertical", boxSizing: "border-box",
            }}
          />
          <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
            {["Ongoing", "Short-term"].map(opt => {
              const selected = editLifecycle === opt;
              return (
                <button
                  key={opt}
                  type="button"
                  onClick={() => setEditLifecycle(opt)}
                  style={{
                    padding: "4px 10px", borderRadius: 999,
                    fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em",
                    cursor: "pointer",
                    background: selected
                      ? (opt === "Short-term" ? "rgba(243,181,63,0.18)" : "rgba(83,196,109,0.18)")
                      : "rgba(255,255,255,0.04)",
                    color: selected
                      ? (opt === "Short-term" ? "#ffe4a5" : "#bff5c8")
                      : T.muted,
                    border: `1px solid ${selected
                      ? (opt === "Short-term" ? "rgba(243,181,63,0.45)" : "rgba(83,196,109,0.45)")
                      : T.border}`,
                  }}
                >{opt}</button>
              );
            })}
            {editLifecycle === "Short-term" && (
              <input
                type="date"
                value={editRevisitBy}
                onChange={e => setEditRevisitBy(e.target.value)}
                style={{
                  background: "rgba(0,0,0,0.25)", color: T.text,
                  border: `1px solid ${T.border}`, borderRadius: 6,
                  padding: "4px 8px", fontSize: 12, colorScheme: "dark",
                }}
              />
            )}
          </div>
          {error && (
            <div style={{ marginTop: 8, fontSize: 12, color: T.red }}>{error}</div>
          )}
          <div style={{ marginTop: 12, display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button
              type="button"
              onClick={() => { resetEditState(); setEditMode(false); }}
              disabled={submitting}
              style={{
                background: "transparent", color: T.muted, border: `1px solid ${T.border}`,
                borderRadius: 6, padding: "6px 12px", fontSize: 12,
                cursor: submitting ? "not-allowed" : "pointer",
              }}
            >Cancel</button>
            <button
              type="button"
              onClick={handleSaveEdit}
              disabled={submitting || !editBody.trim()}
              style={{
                background: T.blue, color: "#0b1f3a", border: "none",
                borderRadius: 6, padding: "6px 14px", fontSize: 12, fontWeight: 700,
                cursor: (submitting || !editBody.trim()) ? "not-allowed" : "pointer",
                opacity: (submitting || !editBody.trim()) ? 0.5 : 1,
              }}
            >{submitting ? "Saving..." : "Save"}</button>
          </div>
        </div>
      ) : (
        <div style={{ fontSize: 14, color: T.text, lineHeight: 1.55, whiteSpace: "pre-wrap", paddingRight: 48 }}>
          {note.body || note.text || ""}
        </div>
      )}

      {/* Footer metadata — hidden during edit to reduce noise */}
      {!editMode && (
        <div style={{
          marginTop: 10, display: "flex", flexWrap: "wrap", alignItems: "center",
          gap: 8, fontSize: 11, color: T.muted,
        }}>
          <span style={pillStyle(authorPill)}>{isPatriot ? "Patriot" : "Customer"}</span>
          {note.author_email && <span>{note.author_email}</span>}
          <span style={pillStyle(lifecyclePill)}>{isShortTerm ? "Short-term" : "Ongoing"}</span>
          {isShortTerm && note.revisit_by && (
            <span>Revisit by {fmtDateOnly(note.revisit_by, undefined, "")}</span>
          )}
          {stale && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5, color: T.red, fontWeight: 600 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: T.red, display: "inline-block" }} />
              Past revisit
            </span>
          )}
          {modified && (
            <span style={{ marginLeft: "auto" }}>Modified: {relativeTime(modified)}</span>
          )}
        </div>
      )}
    </div>
  );
};

export const SettingDetailPanel = ({
  rec, onClose, settingData, onSave, onAddToQueue, onSupport,
  queueIds, queueCount, onRemoveFromQueue, onMarkReviewed,
  onPrev, onNext, hasPrev, hasNext, currentUser = "Patriot", isPatriot = false, notes = [],
  slug,
  onAddNote = async () => {},
  onUpdateNote = async () => {},
  onDeleteNote = async () => {},
  reviews = [],
  onDeleteReview = async () => {}, allowOverrides = true, noteViewerType = isPatriot ? "Patriot" : "Customer",
  currentUserEmail = "",
  // In-flight flags for this rec's optimistic writes (drive button disabled state).
  markReviewPending = false,
  queuePending = false,
}) => {
  // Keyboard navigation: Esc closes, arrow keys step through
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.target && typeof e.target.closest === "function" && e.target.closest('input, textarea, select, [contenteditable=""], [contenteditable="true"], [contenteditable="plaintext-only"]')) return;
      else if (e.key === "ArrowLeft" && hasPrev) onPrev();
      else if (e.key === "ArrowRight" && hasNext) onNext();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose, onPrev, onNext, hasPrev, hasNext]);

  // Hover state for the footer-sized session toggle (In session → Remove).
  const [sessionBtnHover, setSessionBtnHover] = useState(false);
  // Status override form state — syncs with settingData when drawer opens or rec changes
  const [currentOverride, setCurrentOverride] = useState(settingData?.override || null);
  const [currentReason, setCurrentReason] = useState(settingData?.reason || "");
  const [ovNoteLifecycle, setOvNoteLifecycle] = useState("Ongoing");
  const [ovNoteRevisitBy, setOvNoteRevisitBy] = useState("");
  const [ovSaving, setOvSaving] = useState(false);
  const [ovError, setOvError] = useState(null);
  const [ratingsExpanded, setRatingsExpanded] = useState(false);
  useEffect(() => {
    setCurrentOverride(settingData?.override || null);
    setCurrentReason(settingData?.reason || "");
    setOvNoteRevisitBy(settingData?.override && (settingData?.revisit_by) ? String(settingData.revisit_by).split("T")[0] : "");
    setOvError(null);
  }, [rec?.id, settingData]);
  useEffect(() => { setRatingsExpanded(false); }, [rec?.id]);
  // Disposition drives the note's default lifecycle: Planned / Customer
  // Remediated imply a near-term revisit (Short-term); Risk Accepted /
  // Customer Managed Solution default to Ongoing but allow either.
  useEffect(() => {
    if (currentOverride === "planned" || currentOverride === "customer_remediated") {
      setOvNoteLifecycle("Short-term");
    } else if (currentOverride === "risk_accepted" || currentOverride === "customer_managed_solution") {
      setOvNoteLifecycle("Ongoing");
    }
  }, [currentOverride]);

  // Add-note form state (Phase 4 of issue #32)
  const [showAddForm, setShowAddForm] = useState(false);
  const [newNoteBody, setNewNoteBody] = useState("");
  const [newNoteLifecycle, setNewNoteLifecycle] = useState("Ongoing");
  const [newNoteRevisitBy, setNewNoteRevisitBy] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  // Reset form whenever the drawer switches to a different setting
  useEffect(() => {
    setShowAddForm(false);
    setNewNoteBody("");
    setNewNoteLifecycle("Ongoing");
    setNewNoteRevisitBy("");
    setSubmitError(null);
    setSubmitting(false);
  }, [rec?.id]);

  const handleSubmitNewNote = async () => {
    if (!rec || !newNoteBody.trim()) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await onAddNote(rec.id, {
        body: newNoteBody.trim(),
        lifecycle: newNoteLifecycle,
        revisit_by: newNoteLifecycle === "Short-term" && newNoteRevisitBy ? newNoteRevisitBy : undefined,
      });
      setNewNoteBody("");
      setNewNoteLifecycle("Ongoing");
      setNewNoteRevisitBy("");
      setShowAddForm(false);
    } catch (err) {
      setSubmitError(err.message || "Failed to save note");
    } finally {
      setSubmitting(false);
    }
  };

  const cancelAddNote = () => {
    setShowAddForm(false);
    setNewNoteBody("");
    setNewNoteLifecycle("Ongoing");
    setNewNoteRevisitBy("");
    setSubmitError(null);
  };

  const handleSaveOverride = async () => {
    if (!onSave || !rec) return;
    const isShortTerm = ovNoteLifecycle === "Short-term";
    const dateRequired =
      currentOverride === "planned" || currentOverride === "customer_remediated" || isShortTerm;
    if (currentOverride && dateRequired && !ovNoteRevisitBy) {
      setOvError("A date is required for this status.");
      return;
    }
    setOvSaving(true); setOvError(null);
    try {
      // 1) Write the override (keeps reason field populated for existing tooltip)
      await onSave(rec.id, {
        override: currentOverride,
        reason: currentReason,
        updated_by: currentUser,
        updated_at: new Date().toISOString(),
      });
      // 2) If there's reason text and an override, also append a tracked note
      if (currentOverride && currentReason.trim()) {
        const dispoLabel = {
          planned: "Planned",
          risk_accepted: "Risk Accepted",
          customer_managed_solution: "Customer Managed Solution",
          customer_remediated: "Customer Remediated",
        }[currentOverride] || "Status override";
        await onAddNote(rec.id, {
          body: `[${dispoLabel}] ${currentReason.trim()}`,
          lifecycle: ovNoteLifecycle,
          revisit_by: isShortTerm && ovNoteRevisitBy ? ovNoteRevisitBy : undefined,
        });
      }
    } catch (err) {
      setOvError(err.message || "Failed to save");
      setOvSaving(false);
      return;
    }
    setOvSaving(false);
  };

  // Mark this setting reviewed; if already reviewed today, confirm before re-stamping.
  // Mirrors SessionFocusCard's handleMarkReviewedClick / confirm flow (window.confirm here).
  const handleMarkReviewedClick = async () => {
    if (!onMarkReviewed || !rec) return;
    const result = await onMarkReviewed(rec.id);
    if (result && result.needsConfirm) {
      if (window.confirm(`Already marked reviewed today (${result.existingDate}). Mark again?`)) {
        await onMarkReviewed(rec.id, true);
      }
    }
  };

  if (!rec) return null;

  return (
    <>
      {/* Scrim */}
      <div onClick={onClose} style={{
        position:"fixed", top:0, left:0, width:"100vw", height:"100vh",
        background:"rgba(0,0,0,0.45)", zIndex:999, cursor:"pointer",
      }} />

      {/* Drawer */}
      <aside style={{
        position:"fixed", top:0, right:0, height:"100vh", width:800, maxWidth:"92vw",
        background:`linear-gradient(180deg, #0e2746, ${T.bg})`,
        borderLeft:`1px solid ${T.border}`, boxShadow:"-30px 0 60px rgba(0,0,0,0.4)",
        zIndex:1000, display:"flex", flexDirection:"column",
      }}>
        {/* Sticky header */}
        <div style={{
          padding:"18px 22px 16px", borderBottom:`1px solid ${T.border}`,
          background:"rgba(11,31,58,0.96)", backdropFilter:"blur(8px)",
        }}>
          {/* Nav row: prev/next + close */}
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
            <div style={{ display:"flex", gap:4 }}>
              <button onClick={onPrev} disabled={!hasPrev} title="Previous setting (←)" style={{
                width:30, height:30, display:"grid", placeItems:"center",
                borderRadius:8, border:`1px solid ${T.border}`,
                background:"rgba(255,255,255,.04)", color:T.text,
                cursor: hasPrev ? "pointer" : "not-allowed", opacity: hasPrev ? 1 : 0.4,
                fontFamily:"inherit", fontSize:14,
              }}>‹</button>
              <button onClick={onNext} disabled={!hasNext} title="Next setting (→)" style={{
                width:30, height:30, display:"grid", placeItems:"center",
                borderRadius:8, border:`1px solid ${T.border}`,
                background:"rgba(255,255,255,.04)", color:T.text,
                cursor: hasNext ? "pointer" : "not-allowed", opacity: hasNext ? 1 : 0.4,
                fontFamily:"inherit", fontSize:14,
              }}>›</button>
            </div>
            <button onClick={onClose} title="Close (Esc)" style={{
              width:30, height:30, display:"grid", placeItems:"center",
              borderRadius:8, border:`1px solid ${T.border}`,
              background:"rgba(255,255,255,.04)", color:T.text,
              cursor:"pointer", fontFamily:"inherit", fontSize:14,
            }}>✕</button>
          </div>

          {/* Recommendation # + classification dimensions (Product / Category / License) */}
          <div style={{ fontSize:12, color:T.muted, marginBottom:6, display:"flex", gap:6, flexWrap:"wrap", alignItems:"center" }}>
            <span>Recommendation #{rec.id}</span>
            {rec.cat && <span>· <strong style={{ color:T.muted, fontWeight:500 }}>Product:</strong> {rec.cat}</span>}
            {rec.category && <span>· <strong style={{ color:T.muted, fontWeight:500 }}>Security Domain:</strong> {rec.category}</span>}
            {rec.license && <span>· <strong style={{ color:T.muted, fontWeight:500 }}>License:</strong> {rec.license}</span>}
          </div>

          {/* Title */}
          <h2 style={{ fontSize:18, fontWeight:700, lineHeight:1.3, margin:0 }}>{rec.name}</h2>

          {/* Severity + EFFECTIVE status chips. Status pill reflects an active
              override when one is set (reactive via settingData), else rec.status. */}
          <div style={{ display:"flex", gap:6, marginTop:10, flexWrap:"wrap" }}>
            <Badge type={sevBadgeType(rec.sev)}>{sevLabel(rec.sev)}</Badge>
            {(() => {
              // Overrides win over Not Assessed (operator call, 2026-07-28) —
              // mirrors the statusCell precedence; the assessment gap renders
              // as a muted secondary line beside the override badge.
              const ov = settingData?.override;
              const notAssessed = rec.verdict === "not_assessed";
              if (ov) {
                const ovType = {
                  planned: "indigo",
                  risk_accepted: "purple",
                  customer_managed_solution: "teal",
                  customer_remediated: "green",
                }[ov] || "slate";
                return (
                  <>
                    <Badge type={ovType} weight="soft">{dispositionLabel(ov)}</Badge>
                    {notAssessed && (
                      <span
                        title={rec.skip_reason || rec.error_detail || ""}
                        style={{ fontSize:11, color:T.muted, marginLeft:6 }}
                      >· Not assessed this scan</span>
                    )}
                  </>
                );
              }
              if (notAssessed) {
                const b = statusBadge("not_assessed");
                return <Badge type={b.type} weight="soft">{b.label}</Badge>;
              }
              const b = statusBadge(rec.status);
              return <Badge type={b.type} weight="soft">{b.label}</Badge>;
            })()}
          </div>

          {/* Dates strip. (Last-reviewed now shown as a real chip in the footer.) */}
          <div style={{ fontSize:11, color:T.muted, marginTop:10, display:"flex", gap:14, flexWrap:"wrap" }}>
            {rec.first_identified && (
              <span><strong style={{ color:T.muted, fontWeight:500 }}>First flagged:</strong> {fmtInstant(rec.first_identified, {month:"long",day:"numeric",year:"numeric"})}</span>
            )}
          </div>
        </div>

        {/* Scrollable body */}
        <div style={{ flex:1, overflowY:"auto" }}>
          {/* Result panel + value cards — verdict-driven, replaces the old
              recommends/tenant/complexity/disruption box grid. */}
          {(() => {
            const v = ["pass", "fail", "not_assessed", "advisory"].includes(rec.verdict) ? rec.verdict : "not_assessed";
            // accent = verdict/advisory hues (allowed hardcoded per spec); everything else uses T.
            const VS = {
              pass:         { accent:"#53c46d", bg:"rgba(83,196,109,0.10)",  border:"rgba(83,196,109,0.40)",  word:"#bff5c8", icon:"✓", label:"Pass" },
              fail:         { accent:"#e14d57", bg:"rgba(225,77,87,0.10)",   border:"rgba(225,77,87,0.40)",   word:"#ffd5d8", icon:"✕", label:"Fail" },
              not_assessed: { accent:"#94a3b8", bg:"rgba(148,163,184,0.10)", border:"rgba(148,163,184,0.35)", word:"#cbd5e1", icon:"–", label:"Not Assessed" },
              advisory:     { accent:"#64748b", bg:"rgba(100,116,139,0.14)", border:"rgba(100,116,139,0.45)", word:"#cbd5e1", icon:"i", label:"Advisory" },
            };
            const cfg = VS[v];

            // Metric pill: solid dark body so it reads on the colored panel, colored left lip.
            const pill = (label, value, lip, onClick) => {
              const style = {
                display:"flex", flexDirection:"column", background:T.bg,
                border:`1px solid ${T.border}`, borderLeft:`3px solid ${lip}`,
                borderRadius:8, padding:"7px 12px", minWidth:96,
                cursor: onClick ? "pointer" : "default", fontFamily:"inherit", textAlign:"left",
              };
              const content = <>
                <span style={{ fontSize:10, textTransform:"uppercase", letterSpacing:"0.06em", color:T.muted, fontWeight:600, marginBottom:2 }}>{label}</span>
                <span style={{ fontSize:14, fontWeight:700, color:T.text, lineHeight:1.2 }}>{value}</span>
              </>;
              return onClick ? <button key={label} type="button" aria-expanded={ratingsExpanded} aria-controls={ratingsDisclosureId} onClick={onClick} style={style}>{content}</button> : <div key={label} style={style}>{content}</div>;
            };

            const recValue = rec.recommended_value || rec.audit_recommendation;
            const foundValue = rec.result_text || rec.actual_value;
            const complexity = complexityForDisplay(rec);
            const complexityExplanation = typeof rec.complexity_explanation === "string" && rec.complexity_explanation.trim() ? rec.complexity_explanation : null;
            const impactExplanation = typeof rec.impact_risk_explanation === "string" && rec.impact_risk_explanation.trim() ? rec.impact_risk_explanation : null;
            const canExplainRatings = v !== "pass" && (complexityExplanation || impactExplanation);
            const ratingsDisclosureId = `rating-explanations-${rec.id}`;
            const displayedRating = (rating) => typeof rating === "string" && rating.trim() ? rating : "Not rated";

            return (
              <>
                {/* State-colored verdict panel */}
                <div style={{ padding:"18px 22px", borderBottom:`1px solid ${T.border}`, background:"rgba(0,0,0,0.18)" }}>
                  <div style={{ fontSize:11, textTransform:"uppercase", letterSpacing:"0.06em", color:T.muted, marginBottom:10, fontWeight:600 }}>Result</div>
                  <div style={{
                    display:"flex", alignItems:"center", gap:14, flexWrap:"wrap",
                    background:cfg.bg, border:`1px solid ${cfg.border}`, borderRadius:12, padding:"14px 16px",
                  }}>
                    {/* Verdict */}
                    <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                      <span style={{
                        width:26, height:26, borderRadius:999, background:cfg.accent, color:"#0b1f3a",
                        display:"grid", placeItems:"center", fontSize:15, fontWeight:800, lineHeight:1,
                      }}>{cfg.icon}</span>
                      <span style={{ fontSize:18, fontWeight:800, color:cfg.word, letterSpacing:"-0.01em" }}>{cfg.label}</span>
                    </div>
                    {/* Metric pills — hidden when their value is missing AND
                        hidden entirely for satisfied (pass) settings, where
                        remediation complexity/disruption no longer apply. */}
                    {v !== "pass" && (
                      <div style={{ display:"flex", gap:8, marginLeft:"auto", flexWrap:"wrap" }}>
                        {complexity && pill("Complexity", complexity, T.yellow, canExplainRatings ? () => setRatingsExpanded(true) : null)}
                        {rec.user_impact && pill("User disruption", rec.user_impact, T.green, canExplainRatings ? () => setRatingsExpanded(true) : null)}
                      </div>
                    )}
                  </div>
                  {canExplainRatings && <div style={{ marginTop:12 }}>
                  <div style={{ display:"flex", justifyContent:"flex-end" }}>
                    <button type="button" aria-expanded={ratingsExpanded} aria-controls={ratingsDisclosureId} onClick={() => setRatingsExpanded(open => !open)} style={{ background:"none", border:"none", color:T.blue, cursor:"pointer", fontFamily:"inherit", fontSize:13, fontWeight:600, padding:0, display:"inline-flex", alignItems:"center", gap:4 }}>Why these ratings?<svg aria-hidden="true" width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d={ratingsExpanded ? "m4 10 4-4 4 4" : "m4 6 4 4 4-4"} /></svg></button>
                  </div>
                  {ratingsExpanded && <div id={ratingsDisclosureId} style={{ marginTop:8, display:"grid", gap:9 }}>
                    {complexityExplanation && <div style={{ background:"#102e50", border:`1px solid ${T.border}`, borderRadius:9, padding:"11px 13px" }}><div style={{ fontSize:12, fontWeight:650, display:"flex", alignItems:"center", gap:7, marginBottom:5 }}><span aria-hidden="true" style={{ width:7, height:7, borderRadius:999, background:T.yellow, display:"inline-block", flex:"0 0 auto" }} />Complexity — {displayedRating(complexity)}</div><div style={{ whiteSpace:"pre-wrap", overflowWrap:"anywhere", fontSize:12, lineHeight:1.5 }}>{complexityExplanation}</div></div>}
                    {impactExplanation && <div style={{ background:"#102e50", border:`1px solid ${T.border}`, borderRadius:9, padding:"11px 13px" }}><div style={{ fontSize:12, fontWeight:650, display:"flex", alignItems:"center", gap:7, marginBottom:5 }}><span aria-hidden="true" style={{ width:7, height:7, borderRadius:999, background:T.green, display:"inline-block", flex:"0 0 auto" }} />User disruption — {displayedRating(rec.user_impact)}</div><div style={{ whiteSpace:"pre-wrap", overflowWrap:"anywhere", fontSize:12, lineHeight:1.5 }}>{impactExplanation}</div></div>}
                  </div>}
                  </div>}
                </div>

                {/* Patriot Recommends — hidden when no value or recommendation */}
                {recValue && (
                  <div style={{ padding:"14px 22px", borderBottom:`1px solid ${T.border}` }}>
                    <div style={{ background:"rgba(255,255,255,0.04)", border:`1px solid ${T.border}`, borderLeft:`3px solid ${T.blue}`, borderRadius:10, padding:"12px 14px" }}>
                      <div style={{ fontSize:11, textTransform:"uppercase", letterSpacing:"0.06em", color:T.muted, marginBottom:6, fontWeight:600 }}>Patriot Recommends</div>
                      <div style={{ fontSize:15, fontWeight:600, color:T.text, lineHeight:1.45 }}>{recValue}</div>
                    </div>
                  </div>
                )}

                {/* What We Found — lip matches the verdict color */}
                <div style={{ padding:"14px 22px", borderBottom:`1px solid ${T.border}` }}>
                  <div style={{ background:"rgba(255,255,255,0.04)", border:`1px solid ${T.border}`, borderLeft:`3px solid ${cfg.accent}`, borderRadius:10, padding:"12px 14px" }}>
                    <div style={{ fontSize:11, textTransform:"uppercase", letterSpacing:"0.06em", color:T.muted, marginBottom:6, fontWeight:600 }}>What We Found</div>
                    {foundValue ? (
                      <div style={{ fontSize:14, color:T.text, lineHeight:1.55 }}>{foundValue}</div>
                    ) : (
                      <div style={{ fontSize:13, color:T.muted, fontStyle:"italic" }}>Value not captured for this scan</div>
                    )}

                    {/* Why the setting wasn't assessed — the resolved reason
                        (Disposition-aware post-EvidenceV6). */}
                    {v === "not_assessed" && (rec.skip_reason || rec.error_detail) && (
                      <div style={{ marginTop:10, fontSize:13, color:T.muted, lineHeight:1.5 }}>
                        <strong style={{ fontWeight:600 }}>Why not assessed: </strong>
                        {rec.skip_reason || rec.error_detail}
                      </div>
                    )}

                    {/* Collapsible technical detail — only when error_detail is present */}
                    {rec.error_detail && (
                      <details style={{ marginTop:12 }}>
                        <summary style={{ cursor:"pointer", color:T.blue, fontSize:13, fontWeight:600, padding:"6px 10px", borderRadius:6, background:"rgba(68,166,255,0.08)", border:`1px solid ${T.border}`, display:"inline-block", listStyle:"none" }}>Show technical detail</summary>
                        <pre style={{ marginTop:10, padding:"12px 14px", background:"rgba(0,0,0,0.35)", border:`1px solid ${T.border}`, borderRadius:8, fontSize:12, color:T.muted, fontFamily:"ui-monospace, SFMono-Regular, Menlo, monospace", whiteSpace:"pre-wrap", overflow:"auto" }}>{rec.error_detail}</pre>
                      </details>
                    )}
                  </div>
                </div>
              </>
            );
          })()}

          {/* What this setting does */}
          {rec.patriot_advisor_value && (
            <div style={{ padding:"18px 22px", borderBottom:`1px solid ${T.border}` }}>
              <div style={{ fontSize:11, textTransform:"uppercase", letterSpacing:"0.06em", color:T.muted, marginBottom:10, fontWeight:600 }}>What this setting does</div>
              <div style={{ fontSize:14, color:T.text, lineHeight:1.55 }}>{rec.patriot_advisor_value}</div>
            </div>
          )}

          {/* Patriot recommendation */}
          {rec.audit_recommendation && (
            <div style={{ padding:"18px 22px", borderBottom:`1px solid ${T.border}` }}>
              <div style={{ fontSize:11, textTransform:"uppercase", letterSpacing:"0.06em", color:T.muted, marginBottom:10, fontWeight:600 }}>Patriot recommendation</div>
              <div style={{ fontSize:14, color:T.text, lineHeight:1.55 }}>{rec.audit_recommendation}</div>
            </div>
          )}

          {/* Where to change it — breadcrumb + Copy */}
          {rec.remediation_steps && rec.remediation_steps.length > 0 && (
            <div style={{ padding:"18px 22px", borderBottom:`1px solid ${T.border}` }}>
              <div style={{ fontSize:11, textTransform:"uppercase", letterSpacing:"0.06em", color:T.muted, marginBottom:10, fontWeight:600 }}>Where to change it</div>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:10, padding:"10px 14px", background:"rgba(0,0,0,0.25)", border:`1px solid ${T.border}`, borderRadius:8 }}>
                <div style={{ fontSize:13, color:T.text, fontFamily:"ui-monospace, SFMono-Regular, Menlo, monospace" }}>
                  {rec.remediation_steps.map((step, i) => (
                    <span key={i}>
                      {i > 0 && <span style={{ color:T.muted, margin:"0 6px" }}>›</span>}
                      <span style={{ color: i === rec.remediation_steps.length - 1 ? T.text : T.muted, fontWeight: i === rec.remediation_steps.length - 1 ? 600 : 400 }}>{step}</span>
                    </span>
                  ))}
                </div>
                <button onClick={() => navigator.clipboard?.writeText(rec.remediation_steps.join(" › "))} style={{
                  padding:"4px 10px", fontSize:11, fontWeight:600, borderRadius:6,
                  border:`1px solid ${T.border}`, background:"rgba(255,255,255,0.04)", color:T.muted,
                  cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap",
                }}>Copy</button>
              </div>

              {/* Reference doc button — only when help_url or remediation_url is non-null */}
              {(rec.help_url || rec.remediation_url) && (
                <a href={rec.help_url || rec.remediation_url} target="_blank" rel="noopener noreferrer" style={{
                  display:"inline-block", marginTop:10, padding:"7px 13px", fontSize:12, fontWeight:600,
                  borderRadius:8, border:`1px solid ${T.border}`, background:"rgba(255,255,255,0.04)",
                  color:T.text, textDecoration:"none", cursor:"pointer",
                }}>📄 Reference doc ↗</a>
              )}
            </div>
          )}

          {/* Notes (Phase 1 read + Phase 4 write of issue #32). Always open so the "+ New Note" button is visible. */}
          <details open style={{ borderBottom:`1px solid ${T.border}` }}>
            <summary style={{ cursor:"pointer", padding:"14px 22px", listStyle:"none", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <span style={{ display:"flex", alignItems:"center", gap:8 }}>
                <span style={{ fontSize:13, fontWeight:600, color:T.text }}>Notes</span>
                {notes.length > 0 && (
                  <span style={{
                    fontSize:10, fontWeight:700, padding:"2px 7px", borderRadius:999,
                    background:"rgba(68,166,255,0.18)", color:"#9ecdff",
                    border:`1px solid rgba(68,166,255,0.45)`,
                  }}>{notes.length}</span>
                )}
              </span>
              <span style={{ fontSize:11, color:T.muted }}>
                {notes.length === 0 ? "None" : `${notes.length} note${notes.length === 1 ? "" : "s"}`}
              </span>
            </summary>
            <div style={{ padding:"0 22px 16px" }}>
              {/* Add-note affordance: button collapses into an inline form */}
              {!showAddForm ? (
                <button
                  type="button"
                  onClick={() => setShowAddForm(true)}
                  style={{
                    background: "rgba(68,166,255,0.10)",
                    color: T.blue,
                    border: `1px dashed rgba(68,166,255,0.45)`,
                    borderRadius: 8,
                    padding: "8px 12px",
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                    width: "100%",
                    textAlign: "left",
                  }}
                >
                  + New Note
                </button>
              ) : (
                <div style={{
                  background: "rgba(255,255,255,0.04)",
                  border: `1px solid ${T.border}`,
                  borderRadius: 10,
                  padding: "12px 14px",
                }}>
                  <textarea
                    value={newNoteBody}
                    onChange={e => setNewNoteBody(e.target.value)}
                    placeholder="Write a note about this setting..."
                    rows={4}
                    style={{
                      width: "100%",
                      minHeight: 80,
                      background: "rgba(0,0,0,0.25)",
                      color: T.text,
                      border: `1px solid ${T.border}`,
                      borderRadius: 8,
                      padding: "8px 10px",
                      fontSize: 13,
                      lineHeight: 1.55,
                      fontFamily: "inherit",
                      resize: "vertical",
                      boxSizing: "border-box",
                    }}
                  />
                  <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
                    {["Ongoing", "Short-term"].map(opt => {
                      const selected = newNoteLifecycle === opt;
                      return (
                        <button
                          key={opt}
                          type="button"
                          onClick={() => setNewNoteLifecycle(opt)}
                          style={{
                            padding: "4px 10px",
                            borderRadius: 999,
                            fontSize: 11,
                            fontWeight: 600,
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                            cursor: "pointer",
                            background: selected
                              ? (opt === "Short-term" ? "rgba(243,181,63,0.18)" : "rgba(83,196,109,0.18)")
                              : "rgba(255,255,255,0.04)",
                            color: selected
                              ? (opt === "Short-term" ? "#ffe4a5" : "#bff5c8")
                              : T.muted,
                            border: `1px solid ${selected
                              ? (opt === "Short-term" ? "rgba(243,181,63,0.45)" : "rgba(83,196,109,0.45)")
                              : T.border}`,
                          }}
                        >
                          {opt}
                        </button>
                      );
                    })}
                    {newNoteLifecycle === "Short-term" && (
                      <input
                        type="date"
                        value={newNoteRevisitBy}
                        onChange={e => setNewNoteRevisitBy(e.target.value)}
                        style={{
                          background: "rgba(0,0,0,0.25)",
                          color: T.text,
                          border: `1px solid ${T.border}`,
                          borderRadius: 6,
                          padding: "4px 8px",
                          fontSize: 12,
                          colorScheme: "dark",
                        }}
                      />
                    )}
                  </div>
                  {submitError && (
                    <div style={{ marginTop: 8, fontSize: 12, color: T.red }}>{submitError}</div>
                  )}
                  <div style={{ marginTop: 12, display: "flex", justifyContent: "flex-end", gap: 8 }}>
                    <button
                      type="button"
                      onClick={cancelAddNote}
                      disabled={submitting}
                      style={{
                        background: "transparent",
                        color: T.muted,
                        border: `1px solid ${T.border}`,
                        borderRadius: 6,
                        padding: "6px 12px",
                        fontSize: 12,
                        cursor: submitting ? "not-allowed" : "pointer",
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleSubmitNewNote}
                      disabled={submitting || !newNoteBody.trim()}
                      style={{
                        background: T.blue,
                        color: "#0b1f3a",
                        border: "none",
                        borderRadius: 6,
                        padding: "6px 14px",
                        fontSize: 12,
                        fontWeight: 700,
                        cursor: (submitting || !newNoteBody.trim()) ? "not-allowed" : "pointer",
                        opacity: (submitting || !newNoteBody.trim()) ? 0.5 : 1,
                      }}
                    >
                      {submitting ? "Saving..." : "Save"}
                    </button>
                  </div>
                </div>
              )}

              {notes.length === 0 ? (
                <div style={{ fontSize:12, color:T.muted, fontStyle:"italic", padding:"10px 0 4px" }}>
                  No notes for this setting yet.
                </div>
              ) : (
                notes.map(note => (
                  <NoteItem
                    key={note.id}
                    note={note}
                    settingId={rec.id}
                    onEdit={onUpdateNote}
                    onDelete={onDeleteNote}
                    isPatriot={noteViewerType === "Patriot"}
                  />
                ))
              )}
            </div>
          </details>

          {/* Review History (PR 2a) — Dataverse-backed log of session reviews */}
          <details open style={{ borderBottom:`1px solid ${T.border}` }}>
            <summary style={{ cursor:"pointer", padding:"14px 22px", listStyle:"none", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <span style={{ display:"flex", alignItems:"center", gap:8 }}>
                <span style={{ fontSize:13, fontWeight:600, color:T.text }}>Review History</span>
                {reviews.length > 0 && (
                  <span style={{
                    fontSize:10, fontWeight:700, padding:"2px 7px", borderRadius:999,
                    background:"rgba(83,196,109,.18)", color:"#bff5c8",
                    border:`1px solid rgba(83,196,109,.45)`,
                  }}>{reviews.length}</span>
                )}
              </span>
              <span style={{ fontSize:11, color:T.muted }}>
                {reviews.length === 0 ? "Never" : `${reviews.length} review${reviews.length === 1 ? "" : "s"}`}
              </span>
            </summary>
            <div style={{ padding:"0 22px 16px" }}>
              {reviews.length === 0 ? (
                <div style={{ fontSize:13, color:T.muted, fontStyle:"italic", padding:"4px 0" }}>
                  This setting hasn't been reviewed in any session yet.
                </div>
              ) : (
                reviews.map(review => {
                  const myEmail = (currentUserEmail || "").toLowerCase();
                  const rEmail = (review.reviewed_by_email || "").toLowerCase();
                  const myType = isPatriot ? "Patriot" : "Customer";
                  const canDelete = isPatriot || (rEmail && rEmail === myEmail && review.reviewed_by_type === myType);
                  return (
                    <div key={review.id} style={{
                      padding:"8px 0",
                      borderBottom:`1px solid ${T.border}`,
                      display:"flex",
                      justifyContent:"space-between",
                      alignItems:"center",
                      gap:10,
                    }}>
                      <div style={{ minWidth:0, flex:1 }}>
                        <span style={{ fontWeight:600, color:T.text, fontSize:13 }}>{formatReviewDate(review.reviewed_on)}</span>
                        <span style={{ color:T.muted, marginLeft:10, fontSize:12 }}>
                          {review.reviewed_by_type || "—"} · {sessionFocusAuthorName(review.reviewed_by_email) || review.reviewed_by_email || "Unknown"}
                        </span>
                      </div>
                      {canDelete && (
                        <button
                          onClick={() => onDeleteReview(review.id)}
                          aria-label="Delete this review record"
                          title="Delete this review record"
                          style={{
                            width:24, height:24, borderRadius:6, background:"none",
                            border:`1px solid ${T.border}`, color:T.muted, cursor:"pointer",
                            fontFamily:"inherit", fontSize:13, lineHeight:1, flexShrink:0,
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.color = T.red; e.currentTarget.style.borderColor = "rgba(225,77,87,.4)"; }}
                          onMouseLeave={(e) => { e.currentTarget.style.color = T.muted; e.currentTarget.style.borderColor = T.border; }}
                        >×</button>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </details>

          {/* Status overrides collapsible — hidden for Satisfied/Partial settings unless a stale override needs to be cleared */}
          {allowOverrides && ((rec.status !== "implemented" && rec.status !== "partial") || settingData?.override) ? (
          <details open={!!settingData?.override} style={{ borderBottom:`1px solid ${T.border}` }}>
            <summary style={{ cursor:"pointer", padding:"14px 22px", listStyle:"none", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <span style={{ fontSize:13, fontWeight:600, color:T.text }}>Status overrides</span>
              <span style={{ fontSize:11, color:T.muted }}>{settingData?.override ? "Active" : "None set"}</span>
            </summary>
            <div style={{ padding:"0 22px 16px" }}>
              {/* Four mutually exclusive override toggles */}
              {[
                { key: "planned",             label: "Planned",              helper: "Remediation scheduled for a future date. Does not adjust your Risk-Adjusted Score — score updates only after remediation is verified." },
                { key: "risk_accepted",       label: "Risk Accepted",        helper: "Formal decision to accept this risk. Adjusts your Risk-Adjusted Score." },
                { key: "customer_managed_solution",  label: "Customer Managed Solution",   helper: "Third-party solution covers this control (e.g., Proofpoint, Mimecast, KnowBe4). Adjusts your Risk-Adjusted Score." },
                { key: "customer_remediated", label: "Customer Remediated",  helper: "Customer satisfied this; pending Patriot verification. Adjusts your Risk-Adjusted Score until the next scan validates the fix." },
              ].map(opt => {
                const active = currentOverride === opt.key;
                return (
                  <div key={opt.key}>
                  <StatusOverrideOptionButton
                    option={opt}
                    active={active}
                    onToggle={() => setCurrentOverride(active ? null : opt.key)}
                  />

                  {/* Inline note composer (visible directly under the selected
                      disposition). Reason text + a disposition-driven lifecycle;
                      one Save writes both the override and a tracked note. */}
                  {active && (
                    <div style={{ marginTop:12, background:"rgba(255,255,255,0.04)", border:`1px solid ${T.border}`, borderRadius:10, padding:"12px 14px" }}>
                      <label style={{ fontSize:11, color:T.muted, textTransform:"uppercase", letterSpacing:"0.06em", fontWeight:600 }}>Reason</label>
                      <textarea
                        value={currentReason}
                        onChange={e => setCurrentReason(e.target.value)}
                        placeholder="Explain why this status applies..."
                        rows={3}
                        style={{ width:"100%", marginTop:6, padding:"8px 10px", background:"rgba(0,0,0,0.25)", color:T.text, border:`1px solid ${T.border}`, borderRadius:8, fontFamily:"inherit", fontSize:13, resize:"vertical", boxSizing:"border-box" }}
                      />
                      <div style={{ marginTop:10, display:"flex", flexWrap:"wrap", alignItems:"center", gap:8 }}>
                        {(currentOverride === "risk_accepted" || currentOverride === "customer_managed_solution") &&
                          STATUS_OVERRIDE_NOTE_LIFECYCLE_OPTIONS.map(opt => {
                            const selected = ovNoteLifecycle === opt.value;
                            return (
                              <button key={opt.value} type="button"
                                onClick={() => setOvNoteLifecycle(opt.value)}
                                style={{ padding:"4px 10px", borderRadius:999, fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.04em", cursor:"pointer",
                                  background: selected ? (opt.value === "Short-term" ? "rgba(243,181,63,0.18)" : "rgba(83,196,109,0.18)") : "rgba(255,255,255,0.04)",
                                  color: selected ? (opt.value === "Short-term" ? "#ffe4a5" : "#bff5c8") : T.muted,
                                  border:`1px solid ${selected ? (opt.value === "Short-term" ? "rgba(243,181,63,0.45)" : "rgba(83,196,109,0.45)") : T.border}` }}>
                                {opt.label}
                              </button>
                            );
                          })}
                        {(currentOverride === "planned" || currentOverride === "customer_remediated") && (
                          <span style={{ fontSize:11, color:T.muted }}>Revisit date{currentOverride === "customer_remediated" ? " (remediation date)" : ""}:</span>
                        )}
                        {ovNoteLifecycle === "Short-term" && (
                          <input type="date" value={ovNoteRevisitBy} onChange={e => setOvNoteRevisitBy(e.target.value)}
                            style={{ background:"rgba(0,0,0,0.25)", color:T.text, border:`1px solid ${T.border}`, borderRadius:6, padding:"4px 8px", fontSize:12, colorScheme:"dark" }} />
                        )}
                      </div>
                      {ovError && <div style={{ marginTop:8, fontSize:12, color:"#ff8888" }}>{ovError}</div>}
                    </div>
                  )}
                  </div>
                );
              })}

              {/* Audit log: last update */}
              {settingData?.updated_at && (
                <div style={{ marginTop:12, fontSize:11, color:T.muted }}>
                  Last updated by {settingData.updated_by} on {fmtInstant(settingData.updated_at)}
                </div>
              )}
            </div>
          </details>
          ) : null}

          {/* Technical details collapsible */}
          <details style={{ borderBottom:`1px solid ${T.border}` }}>
            <summary style={{ cursor:"pointer", padding:"14px 22px", listStyle:"none", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <span style={{ fontSize:13, fontWeight:600, color:T.text }}>Technical details</span>
              <span style={{ fontSize:11, color:T.muted }}>Default, dates</span>
            </summary>
            <div style={{ padding:"0 22px 18px", display:"grid", gridTemplateColumns:"max-content 1fr", columnGap:18, rowGap:8, fontSize:13 }}>
              <span style={{ color:T.muted }}>Default value</span>
              <span style={{ color:T.text }}>{rec.default_value || "—"}</span>

              <span style={{ color:T.muted }}>First flagged</span>
              <span style={{ color:T.text }}>
                {fmtInstant(rec.first_identified, {month:"long",day:"numeric",year:"numeric"})}
              </span>

              <span style={{ color:T.muted }}>Last reviewed</span>
              <span style={{ color:T.text }}>
                {fmtInstant(rec.last_reviewed, {month:"long",day:"numeric",year:"numeric"}, "Today")}
              </span>
            </div>
          </details>

          {/* Compliance cross-mapping — renders only when backend emits framework data */}
          {Array.isArray(rec.compliance_mappings) && rec.compliance_mappings.length > 0 && (
            <details style={{ borderBottom:`1px solid ${T.border}` }}>
              <summary style={{ cursor:"pointer", padding:"14px 22px", listStyle:"none", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                <span style={{ fontSize:13, fontWeight:600, color:T.text }}>Compliance cross-mapping</span>
                <span style={{ fontSize:11, color:T.muted }}>{rec.compliance_mappings.length} frameworks</span>
              </summary>
              <div style={{ padding:"0 22px 18px", display:"flex", flexWrap:"wrap", gap:6 }}>
                {rec.compliance_mappings.map((m, i) => (
                  <span key={i} style={{
                    padding:"4px 10px", fontSize:11, fontWeight:600,
                    borderRadius:6, border:`1px solid ${T.border}`,
                    background:"rgba(255,255,255,0.04)", color:T.muted,
                  }}>{m.framework}{m.control ? `: ${m.control}` : ""}</span>
                ))}
              </div>
            </details>
          )}
        </div>

        {/* Sticky footer */}
        {(() => {
          const inQueue = !!(queueIds && queueIds.has(rec.id));
          // Defensive: take the most recent review regardless of prop order.
          const lastReview = reviews.length
            ? [...reviews].sort((a, b) => (b.reviewed_on || "").localeCompare(a.reviewed_on || ""))[0]
            : null;
          const reviewLabel = lastReview
            ? (reviews.length === 1
                ? `🕒 Last reviewed ${formatReviewDate(lastReview.reviewed_on)}`
                : `🕒 Last reviewed ${formatReviewDate(lastReview.reviewed_on)} (${reviews.length})`)
            : "";
          const noChange =
            currentOverride === (settingData?.override || null) &&
            currentReason === (settingData?.reason || "") &&
            ovNoteRevisitBy === (settingData?.override && settingData?.revisit_by ? String(settingData.revisit_by).split("T")[0] : "");

          // Footer-sized session toggle — mirrors QueueAddBtn's three states and
          // green/red colors, but at the footer button height (matches Support/Save).
          // QueueAddBtn itself stays compact for the recs table; we re-render here.
          const isFull = queueCount >= MAX_QUEUE;
          const sessionBtnBase = { padding:"9px 16px", fontSize:13, fontWeight:600, borderRadius:8, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" };
          let sessionEl = null;
          if (inQueue && onRemoveFromQueue) {
            sessionEl = (
              <button
                onMouseEnter={() => setSessionBtnHover(true)} onMouseLeave={() => setSessionBtnHover(false)}
                onClick={() => onRemoveFromQueue(rec.id)}
                title="In your Next Session Focus — click to remove"
                style={{ ...sessionBtnBase,
                  background: sessionBtnHover ? "rgba(225,77,87,.15)" : "rgba(83,196,109,.12)",
                  color: sessionBtnHover ? T.red : T.green,
                  border: `1px solid ${sessionBtnHover ? "rgba(225,77,87,.3)" : "rgba(83,196,109,.3)"}`,
                  borderLeft: `3px solid ${sessionBtnHover ? "rgba(225,77,87,.6)" : T.green}`,
                }}
              >{sessionBtnHover ? "Remove" : "✔ In session"}</button>
            );
          } else if (!inQueue && onAddToQueue && rec.status === "open") {
            sessionEl = isFull
              ? <span title={`Session focus is full (${MAX_QUEUE}/${MAX_QUEUE})`} style={{ fontSize:12, color:T.muted, fontWeight:600, opacity:0.5 }}>Session full</span>
              : (
                <button onClick={() => onAddToQueue(rec)} disabled={queuePending}
                  title={queuePending ? "Adding to session…" : "Add this item to your Next Session Focus"}
                  style={{ ...sessionBtnBase,
                    background:"rgba(255,255,255,0.04)", color:T.muted, border:`1px solid ${T.border}`,
                    opacity: queuePending ? 0.5 : 1, cursor: queuePending ? "not-allowed" : "pointer",
                  }}>{queuePending ? "Adding…" : "+ Add to Session"}</button>
              );
          }
          // (sessionEl stays null for satisfied settings not in the queue — matches QueueAddBtn.)
          // Mark Reviewed is a Patriot-only action available on every setting (not just
          // in-session ones); lastReview keeps the passive label visible to customers too.
          const reviewGroup = (isPatriot && onMarkReviewed) || lastReview;

          return (
            <div style={{
              padding:"14px 22px", borderTop:`1px solid ${T.border}`,
              background:"rgba(11,31,58,0.96)", backdropFilter:"blur(8px)",
              display:"flex", gap:10, justifyContent:"space-between", alignItems:"center",
            }}>
              {/* Left: session STATE (toggle) | review STATUS (mark reviewed + date). */}
              <div style={{ display:"flex", gap:12, alignItems:"center", flexWrap:"wrap" }}>
                {sessionEl}
                {sessionEl && reviewGroup && (
                  <span style={{ width:1, height:24, background:T.border, alignSelf:"center" }} />
                )}
                {reviewGroup && (
                  <div style={{ display:"flex", gap:10, alignItems:"center", flexWrap:"wrap" }}>
                    {isPatriot && onMarkReviewed && (
                      <button onClick={handleMarkReviewedClick} disabled={markReviewPending}
                        title={markReviewPending ? "Marking reviewed…" : "Mark this setting reviewed today"} style={{
                        padding:"9px 16px", fontSize:13, fontWeight:600, borderRadius:8,
                        border:`1px solid rgba(68,166,255,0.4)`, background:"rgba(68,166,255,0.18)", color:T.blue,
                        cursor: markReviewPending ? "not-allowed" : "pointer", opacity: markReviewPending ? 0.5 : 1,
                        fontFamily:"inherit", display:"inline-flex", alignItems:"center", gap:6, whiteSpace:"nowrap",
                      }}>{markReviewPending ? "Marking…" : "📋 Mark Reviewed"}</button>
                    )}
                    {lastReview && (
                      // Passive label — info, not an action. No border/fill.
                      <span style={{ fontSize:11, color:T.muted, display:"inline-flex", alignItems:"center", gap:3, whiteSpace:"nowrap" }}>{reviewLabel}</span>
                    )}
                  </div>
                )}
              </div>

              {/* Right: support + save */}
              <div style={{ display:"flex", gap:10, alignItems:"center" }}>
                {onSupport && (
                  <button onClick={() => onSupport(rec)} style={{
                    padding:"9px 16px", fontSize:13, fontWeight:600, borderRadius:8,
                    border:`1px solid rgba(68,166,255,0.4)`, background:"rgba(68,166,255,0.18)", color:T.blue,
                    cursor:"pointer", fontFamily:"inherit",
                  }}>🛠 Support</button>
                )}
                {allowOverrides && <button onClick={handleSaveOverride} disabled={noChange || ovSaving} style={{
                  padding:"9px 16px", fontSize:13, fontWeight:600, borderRadius:8,
                  border:`1px solid ${T.blue}`, background:T.blue, color:"#fff",
                  cursor: (noChange || ovSaving) ? "not-allowed" : "pointer",
                  opacity: (noChange || ovSaving) ? 0.5 : 1,
                  fontFamily:"inherit",
                }}>{ovSaving ? "Saving…" : "Save"}</button>}
              </div>
            </div>
          );
        })()}
      </aside>
    </>
  );
};


/* ══════════════════════════════════════════════
   PAGE: RECOMMENDATIONS
   ══════════════════════════════════════════════ */
const QueueAddBtn = ({ rec, inQueue, isFull, onAdd, onRemove, pending = false }) => {
  const [hovered, setHovered] = useState(false);
  if (!inQueue && rec.status !== "open") return null;
  if (!inQueue && isFull) return (
    <span title={`Session focus is full (${MAX_QUEUE}/${MAX_QUEUE})`} style={{ fontSize:10, color:T.muted, fontWeight:600, opacity:0.5 }}>Full</span>
  );
  if (inQueue) {
    return (
      <button
        onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
        onClick={(e) => { e.stopPropagation(); onRemove(rec.id); }}
        title="In your Next Session Focus \u2014 click to remove"
        style={{
          padding:"3px 8px", fontSize:10, fontWeight:700, borderRadius:6, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap", transition:".15s",
          background: hovered ? "rgba(225,77,87,.15)" : "rgba(83,196,109,.12)",
          color: hovered ? T.red : T.green,
          border: `1px solid ${hovered ? "rgba(225,77,87,.3)" : "rgba(83,196,109,.3)"}`,
          borderLeft: `3px solid ${hovered ? "rgba(225,77,87,.6)" : T.green}`,
        }}
      >{hovered ? "Remove" : "\u2714 In session"}</button>
    );
  }
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onAdd(rec); }}
      disabled={pending}
      title={pending ? "Adding to session…" : "Add this item to your Next Session Focus"}
      style={{
        padding:"3px 8px", fontSize:10, fontWeight:700, background:"rgba(255,255,255,0.04)",
        color:T.muted, border:`1px solid ${T.border}`, borderRadius:6,
        cursor: pending ? "not-allowed" : "pointer", opacity: pending ? 0.5 : 1, fontFamily:"inherit",
        whiteSpace:"nowrap", transition:".15s",
      }}
      onMouseEnter={pending ? undefined : (e => { e.currentTarget.style.background = "rgba(83,196,109,.12)"; e.currentTarget.style.color = T.green; e.currentTarget.style.borderColor = "rgba(83,196,109,.3)"; })}
      onMouseLeave={pending ? undefined : (e => { e.currentTarget.style.background = "rgba(255,255,255,0.04)"; e.currentTarget.style.color = T.muted; e.currentTarget.style.borderColor = T.border; })}
    >{pending ? "Adding…" : "+ Session"}</button>
  );
};

// \u2500\u2500 Session focus helpers \u2500\u2500
function sessionFocusAuthorName(email) {
  if (!email) return "";
  const local = String(email).split("@")[0] || "";
  if (!local) return "";
  if (local.includes(".")) {
    const first = local.split(".")[0];
    return first.charAt(0).toUpperCase() + first.slice(1).toLowerCase();
  }
  return local.charAt(0).toUpperCase() + local.slice(1).toLowerCase();
}

// \u2500\u2500 Session Focus expanded card (sticky above the recs table) \u2500\u2500
const SessionFocusCard = ({ items, recs, settingDetails, notesBySettingId, reviewsBySettingId, onMarkReviewed, currentUserEmail, onCollapse, onItemClick, onRemove, onUpdate, onReorder, onNavQueueTab, fullPage = false, pendingReview = new Set(), isPatriot = false }) => {
  // { settingId, existingDate } when the soft de-dup confirmation is open.
  const [confirmModalState, setConfirmModalState] = useState(null);
  const handleMarkReviewedClick = async (settingId) => {
    if (!onMarkReviewed) return;
    const result = await onMarkReviewed(settingId);
    if (result && result.needsConfirm) {
      setConfirmModalState({ settingId, existingDate: result.existingDate });
    }
  };
  const confirmAndMarkAgain = async () => {
    if (!confirmModalState || !onMarkReviewed) return;
    const { settingId } = confirmModalState;
    setConfirmModalState(null);
    await onMarkReviewed(settingId, true);
  };
  const isReviewedToday = (settingId) => {
    if (!currentUserEmail) return false;
    // Local date (not UTC) so the pill is correct in the user's local time.
    const today = localDateString();
    const myEmail = currentUserEmail.toLowerCase();
    const list = (reviewsBySettingId && reviewsBySettingId.get(settingId)) || [];
    return list.some(r => {
      const rEmail = (r.reviewed_by_email || "").toLowerCase();
      const rDate = (r.reviewed_on || "").slice(0, 10);
      return rEmail === myEmail && rDate === today;
    });
  };
  const recById = useMemo(() => {
    const m = new Map();
    for (const r of recs || []) m.set(r.id, r);
    return m;
  }, [recs]);
  const realNotesCount = (settingId) => (notesBySettingId?.get(settingId) || []).length;
  const activeItems = items
    .filter(i => i.status === "Active")
    .sort((a, b) => (a.order_index || 0) - (b.order_index || 0));
  const ordered = activeItems;

  const [dragId, setDragId] = useState(null);
  const [dropId, setDropId] = useState(null);

  const handleDragStart = (e, id) => {
    setDragId(id);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", id);
  };
  const handleDragOver = (e, id) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDropId(id);
  };
  const handleDragLeave = () => { setDropId(null); };
  const handleDrop = (e, targetId) => {
    e.preventDefault();
    if (!dragId || dragId === targetId) { setDragId(null); setDropId(null); return; }
    const activeIds = activeItems.map(i => i.id);
    const dragIdx = activeIds.indexOf(dragId);
    const dropIdx = activeIds.indexOf(targetId);
    if (dragIdx < 0 || dropIdx < 0) { setDragId(null); setDropId(null); return; }
    const next = [...activeIds];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(dropIdx, 0, moved);
    onReorder(next);
    setDragId(null);
    setDropId(null);
  };
  const handleDragEnd = () => { setDragId(null); setDropId(null); };

  // Mirror the recs table exactly so columns line up.
  const thStyle = { padding:"10px 8px", borderBottom:`1px solid ${T.border}`, textAlign:"left", fontSize:12, color:T.muted, fontWeight:600, textTransform:"uppercase", letterSpacing:".04em" };
  const tdStyle = { padding:"10px 8px", borderBottom:`1px solid ${T.border}`, fontSize:13 };


  const cardStyle = fullPage ? {
    background: T.panel,
    border:`1px solid ${T.border}`,
    borderRadius:12,
    overflow:"hidden",
  } : {
    background: T.panel,
    border:"1px solid rgba(83,196,109,.30)",
    borderLeft:"3px solid rgba(83,196,109,.60)",
    borderRadius:14,
    boxShadow:"0 10px 40px rgba(0,0,0,0.55)",
    overflow:"hidden",
  };

  return (
    <div style={cardStyle}>
      {!fullPage && (
      <>
      {/* Card header \u2014 sticky within card */}
      <div style={{
        display:"flex", alignItems:"center", gap:10, padding:"12px 14px",
        background:`linear-gradient(180deg, rgba(83,196,109,.18), rgba(83,196,109,.08))`,
        borderBottom:`1px solid rgba(83,196,109,.30)`,
      }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.green} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/>
        </svg>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:14, fontWeight:700, color:T.text }}>Next session focus</div>
          <div style={{ fontSize:11, color:T.muted, marginTop:2 }}>
            {activeItems.length} of {MAX_QUEUE} items · drag to reorder · click to open detail
          </div>
        </div>
        <button onClick={(e) => { e.stopPropagation(); onNavQueueTab(); }} style={{
          padding:"4px 10px", fontSize:11, fontWeight:600, borderRadius:8,
          background:"none", border:"none", color:T.blue, cursor:"pointer", fontFamily:"inherit",
        }}>Session mode →</button>
        <button onClick={(e) => { e.stopPropagation(); onCollapse(); }} style={{
          padding:"4px 10px", fontSize:11, fontWeight:700, borderRadius:8,
          background:"rgba(255,255,255,.06)", color:T.text,
          border:`1px solid ${T.border}`, cursor:"pointer", fontFamily:"inherit",
        }}>Collapse</button>
      </div>
      </>
      )}
      {/* Card body */}
      <div style={fullPage ? { overflowY:"visible" } : { maxHeight:320, overflowY:"auto" }}>
        <table style={{ width:"100%", borderCollapse:"collapse", tableLayout:"fixed" }}>
          <colgroup>
            <col style={{ width:24 }} />
            <col style={{ width:40 }} />
            <col style={{ width:150 }} />
            <col />
            <col style={{ width:80 }} />
            <col style={{ width:115 }} />
            <col style={{ width:50 }} />
            <col style={{ width:260 }} />
          </colgroup>
          <thead>
            <tr>
              <th style={{ ...thStyle, width:24, position:"sticky", top:0, background:T.panel, zIndex:1 }}></th>
              <th style={{ ...thStyle, width:40, position:"sticky", top:0, background:T.panel, zIndex:1 }}>#</th>
              <th style={{ ...thStyle, width:150, position:"sticky", top:0, background:T.panel, zIndex:1 }}>Status</th>
              <th style={{ ...thStyle, position:"sticky", top:0, background:T.panel, zIndex:1 }}>Recommendation</th>
              <th style={{ ...thStyle, width:80, position:"sticky", top:0, background:T.panel, zIndex:1 }}>Product</th>
              <th style={{ ...thStyle, width:115, position:"sticky", top:0, background:T.panel, zIndex:1 }}>Severity</th>
              <th style={{ ...thStyle, width:50, position:"sticky", top:0, background:T.panel, zIndex:1 }}></th>
              <th style={{ ...thStyle, width:260, position:"sticky", top:0, background:T.panel, zIndex:1 }}>Session Type</th>
            </tr>
          </thead>
          <tbody>
            {ordered.length === 0 && (
              <tr>
                <td colSpan={8} style={{ padding:"18px 14px", fontSize:12, color:T.muted, textAlign:"center" }}>
                  No items in your session focus. Use the "+ Session" button on a row to add one.
                </td>
              </tr>
            )}
            {ordered.map((item) => {
              const rec = recById.get(item.setting_id);
              const reviewedToday = isReviewedToday(item.setting_id);
              const reviewPending = pendingReview.has(item.setting_id);
              const authorIsPatriot = item.author_type === "Patriot";
              const isDropTarget = dropId === item.id && dragId !== item.id;
              const sd = settingDetails?.[item.setting_id];
              const statusCell = (() => {
                // Overrides win over Not Assessed (operator call, 2026-07-28);
                // the gap rides in the tooltip — mirrors the recs table.
                const naTip = rec?.verdict === "not_assessed"
                  ? `Not assessed this scan${(rec.skip_reason || rec.error_detail) ? `: ${rec.skip_reason || rec.error_detail}` : ""}`
                  : null;
                const tipWith = (base) => [base || "", naTip].filter(Boolean).join(" — ");
                if (sd?.override === "risk_accepted")        return { ...statusBadge("exception"),   tip: tipWith(sd.reason) };
                if (sd?.override === "customer_remediated")  return { ...statusBadge("pending"),     tip: tipWith(sd.reason) };
                if (sd?.override === "planned")              return { ...statusBadge("planned"),     tip: tipWith(sd.reason) };
                if (sd?.override === "customer_managed_solution")   return { ...statusBadge("managed_solution"),   tip: tipWith(sd.reason) };
                if (rec?.verdict === "not_assessed") {
                  return { ...statusBadge("not_assessed"), tip: rec.skip_reason || rec.error_detail || "" };
                }
                if (rec) return { ...statusBadge(rec.status), tip:"" };
                return null;
              })();
              const nc = realNotesCount(item.setting_id);
              const authorBadge = (
                <span style={{
                  display:"inline-block",
                  padding:"1px 6px", borderRadius:999, fontSize:10,
                  background: authorIsPatriot ? "rgba(68,166,255,.15)" : "rgba(243,181,63,.15)",
                  color: authorIsPatriot ? T.blue : T.yellow,
                  border:`1px solid ${authorIsPatriot ? "rgba(68,166,255,.3)" : "rgba(243,181,63,.3)"}`,
                  fontWeight:600,
                }}>{authorIsPatriot ? "Patriot" : "Customer"} · {sessionFocusAuthorName(item.author_email) || "Unknown"}</span>
              );
              const reviewList = (reviewsBySettingId && reviewsBySettingId.get(item.setting_id)) || [];
              const lastReviewedBadge = reviewList.length > 0 ? (() => {
                const last = reviewList[0];
                const lastDate = formatReviewDate(last.reviewed_on);
                const label = reviewList.length === 1
                  ? `Last reviewed ${lastDate}`
                  : `Last reviewed ${lastDate} (${reviewList.length} total)`;
                const tipLines = reviewList.slice(0, 3).map(r => {
                  const d = formatReviewDate(r.reviewed_on);
                  const t = r.reviewed_by_type || "—";
                  const who = sessionFocusAuthorName(r.reviewed_by_email) || r.reviewed_by_email || "Unknown";
                  return `${d} · ${t} · ${who}`;
                }).join("\n");
                return (
                  <span title={tipLines} style={{
                    display:"inline-block",
                    padding:"1px 6px", borderRadius:999, fontSize:10,
                    color: T.muted,
                    background:"rgba(255,255,255,.04)",
                    border:`1px solid ${T.border}`,
                  }}>{label}</span>
                );
              })() : null;
              return (
                <tr
                  key={item.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, item.id)}
                  onDragOver={(e) => handleDragOver(e, item.id)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, item.id)}
                  onDragEnd={handleDragEnd}
                  style={{
                    opacity: dragId === item.id ? 0.4 : 1,
                    background: isDropTarget ? "rgba(83,196,109,.06)" : "transparent",
                    boxShadow: isDropTarget ? `inset 0 2px 0 ${T.green}` : "none",
                    transition:"opacity .15s",
                  }}
                >
                  <td
                    style={{ ...tdStyle, textAlign:"center", color:T.muted, fontSize:16, userSelect:"none", cursor:"grab", lineHeight:1 }}
                    title="Drag to reorder"
                    aria-hidden="true"
                  >⠿</td>
                  <td style={{ ...tdStyle, color:T.muted }}>{item.setting_id}</td>
                  <td style={tdStyle}>
                    {statusCell ? (
                      <span title={statusCell.tip || ""}>
                        <Badge type={statusCell.type} weight="soft">{statusCell.label}</Badge>
                      </span>
                    ) : <span style={{ color:T.muted }}>—</span>}
                  </td>
                  <td style={tdStyle}>
                    {rec ? (
                      <>
                        <div
                          onClick={() => onItemClick(item)}
                          style={{
                            cursor:"pointer",
                            textDecoration:"underline dotted",
                            textUnderlineOffset:3,
                            color:T.text,
                            overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap",
                          }}
                        >{rec.name}</div>
                        <div style={{ marginTop:4, display:"flex", gap:6, alignItems:"center", flexWrap:"wrap" }}>
                          {authorBadge}
                          {lastReviewedBadge}
                        </div>
                      </>
                    ) : (
                      <>
                        <div style={{ color:T.muted, fontStyle:"italic" }}>Setting unavailable</div>
                        <div style={{ marginTop:4, display:"flex", gap:6, alignItems:"center", flexWrap:"wrap" }}>
                          {authorBadge}
                          {lastReviewedBadge}
                        </div>
                      </>
                    )}
                  </td>
                  <td style={{ ...tdStyle, color:T.muted }}>{rec?.cat || "—"}</td>
                  <td style={{ ...tdStyle, paddingRight:12 }}>{rec?.sev ? <Badge type={sevBadgeType(rec.sev)}>{sevLabel(rec.sev)}</Badge> : <span style={{ color:T.muted }}>—</span>}</td>
                  <td style={tdStyle}>
                    <button onClick={(e) => { e.stopPropagation(); onItemClick(item); }} style={{ background:"none", border:"none", cursor:"pointer", display:"flex", alignItems:"center", gap:4, padding:0 }} title={nc > 0 ? `${nc} note${nc === 1 ? "" : "s"}` : "No notes"}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill={nc > 0 ? T.text : "none"} stroke={nc > 0 ? T.text : "rgba(255,255,255,0.2)"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                      {nc > 0 && <span style={{ fontSize:11, fontWeight:700, color:T.text }}>{nc}</span>}
                    </button>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                      <select
                        value={item.session_type === "Review" ? "Review" : "Working"}
                        onChange={(e) => { e.stopPropagation(); onUpdate(item.id, { session_type: e.target.value }); }}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          padding:"4px 6px", background:"rgba(255,255,255,.06)", border:`1px solid ${T.border}`, borderRadius:8,
                          color:T.text, fontFamily:"inherit", fontSize:11, cursor:"pointer",
                        }}
                      >
                        <option value="Working" style={{ background:T.panel }}>Working</option>
                        <option value="Review" style={{ background:T.panel }}>Review</option>
                      </select>
                      {isPatriot && onMarkReviewed && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleMarkReviewedClick(item.setting_id); }}
                          disabled={reviewPending}
                          title={reviewPending ? "Marking reviewed…" : (reviewedToday ? "You've marked this reviewed today. Click to mark again." : "Mark this setting as reviewed in today's session")}
                          style={{
                            padding:"4px 8px", fontSize:11, fontWeight:600, borderRadius:8,
                            background: reviewedToday ? "rgba(255,255,255,.04)" : "rgba(83,196,109,.10)",
                            color: reviewedToday ? T.muted : T.green,
                            border: reviewedToday ? `1px solid ${T.border}` : `1px solid rgba(83,196,109,.25)`,
                            cursor: reviewPending ? "not-allowed" : "pointer", opacity: reviewPending ? 0.5 : 1,
                            fontFamily:"inherit", whiteSpace:"nowrap",
                            display:"inline-flex", alignItems:"center", gap:4,
                          }}
                        >{reviewPending ? "Marking…" : (reviewedToday ? "✓ Reviewed Today" : "📋 Mark Reviewed")}</button>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); onRemove(item.id); }}
                        aria-label="Remove from session focus"
                        title="Remove"
                        style={{
                          width:26, height:26, borderRadius:6,
                          background:"rgba(225,77,87,.10)",
                          border:"1px solid rgba(225,77,87,.30)",
                          color:T.muted, cursor:"pointer",
                          fontFamily:"inherit", fontSize:13, lineHeight:1, flexShrink:0,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = T.red;
                          e.currentTarget.style.borderColor = "rgba(225,77,87,.6)";
                          e.currentTarget.style.background = "rgba(225,77,87,.20)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = T.muted;
                          e.currentTarget.style.borderColor = "rgba(225,77,87,.30)";
                          e.currentTarget.style.background = "rgba(225,77,87,.10)";
                        }}
                      >×</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {confirmModalState && (
        <div
          onClick={() => setConfirmModalState(null)}
          style={{
            position:"fixed", top:0, left:0, width:"100vw", height:"100vh",
            background:"rgba(0,0,0,0.55)", zIndex:1001,
            display:"flex", alignItems:"center", justifyContent:"center",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: T.panel, border:`1px solid ${T.border}`,
              borderRadius:12, padding:"22px 24px", width:440, maxWidth:"92vw",
              boxShadow:"0 30px 70px rgba(0,0,0,0.55)",
            }}
          >
            <div style={{ fontSize:15, fontWeight:700, color:T.text, marginBottom:10 }}>
              Already marked reviewed today
            </div>
            <div style={{ fontSize:13, color:T.muted, lineHeight:1.5, marginBottom:18 }}>
              You marked this setting as reviewed on {confirmModalState.existingDate}. Mark again?
            </div>
            <div style={{ display:"flex", justifyContent:"flex-end", gap:8 }}>
              <button
                type="button"
                onClick={() => setConfirmModalState(null)}
                style={{
                  background:"transparent", color:T.muted, border:`1px solid ${T.border}`,
                  borderRadius:8, padding:"7px 14px", fontSize:13, cursor:"pointer", fontFamily:"inherit",
                }}
              >Cancel</button>
              <button
                type="button"
                onClick={confirmAndMarkAgain}
                style={{
                  background: T.green, color:"#0b1f3a", border:"none",
                  borderRadius:8, padding:"7px 14px", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"inherit",
                }}
              >Yes, mark again</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// \u2500\u2500 Session Focus button (top-right of Recommendations) \u2500\u2500
const SessionFocusButton = ({ count, max, isOpen, onToggle, hasError }) => {
  const isFull = count >= max;
  return (
    <button
      onClick={onToggle}
      aria-expanded={isOpen}
      title={hasError ? "Session focus failed to load \u2014 click to retry" : "Open Session Focus panel"}
      style={{
        padding:"6px 12px", borderRadius:999,
        border:`1px solid ${isFull ? "rgba(243,181,63,.4)" : "rgba(83,196,109,.3)"}`,
        background: isOpen
          ? (isFull ? "rgba(243,181,63,.22)" : "rgba(83,196,109,.22)")
          : (isFull ? "rgba(243,181,63,.1)" : "rgba(83,196,109,.1)"),
        color: isFull ? T.yellow : T.green,
        cursor:"pointer", fontFamily:"inherit", fontSize:11, fontWeight:700, transition:".15s",
        display:"inline-flex", alignItems:"center", gap:6,
      }}
    >
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/>
      </svg>
      <span>Session Focus: {count} / {max} {isOpen ? "▲" : "▼"}</span>
    </button>
  );
};

const SupportBtn = ({ rec, onSingle, onBulk }) => {
  const [open, setOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState(null);
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const updateMenuPosition = useCallback(() => {
    const button = buttonRef.current;
    if (!button || typeof window === "undefined") return;
    const rect = button.getBoundingClientRect();
    const menuHeight = 84;
    const menuWidth = Math.min(210, window.innerWidth - 16);
    const left = Math.max(8, Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8));
    const placeAbove = window.innerHeight - rect.bottom < menuHeight + 8 && rect.top > menuHeight + 8;
    const top = Math.max(8, placeAbove ? rect.top - menuHeight - 4 : Math.min(rect.bottom + 4, window.innerHeight - menuHeight - 8));
    setMenuPosition({ left, top, width:menuWidth });
  }, []);
  useEffect(() => {
    if (!open) return;
    updateMenuPosition();
    menuRef.current?.querySelector("button")?.focus();
    const dismissOutside = (event) => {
      if (rootRef.current?.contains(event.target) || menuRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      buttonRef.current?.focus();
    };
    document.addEventListener("mousedown", dismissOutside, true);
    document.addEventListener("scroll", updateMenuPosition, true);
    document.addEventListener("keydown", closeOnEscape);
    window.addEventListener("resize", updateMenuPosition);
    window.visualViewport?.addEventListener("resize", updateMenuPosition);
    window.visualViewport?.addEventListener("scroll", updateMenuPosition);
    return () => {
      document.removeEventListener("mousedown", dismissOutside, true);
      document.removeEventListener("scroll", updateMenuPosition, true);
      document.removeEventListener("keydown", closeOnEscape);
      window.removeEventListener("resize", updateMenuPosition);
      window.visualViewport?.removeEventListener("resize", updateMenuPosition);
      window.visualViewport?.removeEventListener("scroll", updateMenuPosition);
    };
  }, [open, updateMenuPosition]);
  const itemStyle = { display:"block", width:"100%", padding:"7px 10px", background:"none", border:"none", color:T.text, fontSize:11,
    fontFamily:"inherit", cursor:"pointer", textAlign:"left", borderRadius:4, transition:"background .1s" };
  return (
    <>
      <div ref={rootRef} style={{ position:"relative", display:"inline-block" }}>
      <button ref={buttonRef} type="button" aria-haspopup="true" aria-expanded={open} aria-controls={`support-actions-${rec.id}`} onClick={(e) => { e.stopPropagation(); if (!open) updateMenuPosition(); setOpen(value => !value); }} style={{
        padding:"3px 8px", fontSize:10, fontWeight:700, background:"rgba(68,166,255,.15)",
        color:T.blue, border:"1px solid rgba(68,166,255,.3)", borderRadius:6, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap",
      }}>{"\uD83D\uDD27"} Support</button>
      </div>
      {open && menuPosition && createPortal(
        <div id={`support-actions-${rec.id}`} ref={menuRef} aria-label="Support actions" onKeyDown={event => {
          if (event.key !== "Tab") return;
          const actions = [...(menuRef.current?.querySelectorAll("button") || [])];
          const active = document.activeElement;
          if (event.shiftKey && active === actions[0]) {
            event.preventDefault();
            setOpen(false);
            buttonRef.current?.focus();
          } else if (!event.shiftKey && active === actions[actions.length - 1]) {
            event.preventDefault();
            setOpen(false);
            const focusable = [...document.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])')]
              .filter(element => !menuRef.current?.contains(element) && element.getAttribute("aria-hidden") !== "true");
            const index = focusable.indexOf(buttonRef.current);
            (focusable[index + 1] || buttonRef.current)?.focus();
          }
        }} style={{
          position:"fixed", top:menuPosition.top, left:menuPosition.left, width:menuPosition.width, maxWidth:"calc(100vw - 16px)",
          background:T.panel, border:`1px solid ${T.border}`, borderRadius:10, boxShadow:"0 8px 24px rgba(0,0,0,0.45)",
          zIndex:10000, padding:4, overflow:"hidden",
        }}>
          <button style={itemStyle}
            onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.05)"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "none"; }}
            onClick={(e) => { e.stopPropagation(); setOpen(false); onSingle(rec); }}
          >Create ticket for this item</button>
          <button style={itemStyle}
            onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.05)"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "none"; }}
            onClick={(e) => { e.stopPropagation(); setOpen(false); onBulk(rec); }}
          >Select multiple items for ticket</button>
        </div>, document.body
      )}
    </>
  );
};

// Per-severity "+N more" deep-links from the top severity cards (Overview).
// Each lands as its severity chip + the Needs Action composite, so the card
// headline (open AND no credit-granting override) equals the landed
// "Showing N". Bare "critical"/"high"/"medium" filter keys keep raw severity
// semantics for other callers (donut slices, legacy deep-links).
const NEEDS_ACTION_SEV_KEYS = {
  critical_needs_action: "critical",
  high_needs_action: "high",
  medium_needs_action: "medium",
};

const RECS_COLUMN_DEFINITIONS = [
  { id:"number", label:"#", defaultVisible:true, required:false, sort:"num" },
  { id:"status", label:"Status", defaultVisible:true, required:false, sort:"status" },
  { id:"recommendation", label:"Recommendation", defaultVisible:true, required:true, sort:"rec" },
  { id:"dimension", label:"Product", defaultVisible:true, required:false, sort:"cat" },
  { id:"severity", label:"Severity", defaultVisible:true, required:false, sort:"sev" },
  { id:"complexity", label:"Complexity", defaultVisible:false, required:false },
  { id:"userDisruption", label:"User disruption", defaultVisible:false, required:false },
  { id:"notes", label:"Notes", defaultVisible:true, required:false },
  { id:"session", label:"Session", defaultVisible:true, required:false },
  { id:"support", label:"Support", defaultVisible:true, required:true },
  { id:"age", label:"Age", defaultVisible:true, required:false, sort:"age" },
];
const RECS_COLUMN_IDS = RECS_COLUMN_DEFINITIONS.map(column => column.id);
// Budget includes each column's cell padding. Keep the wide budget independent
// of compact state so a resize never flips between two thresholds.
const RECS_COLUMN_WIDE_WIDTH = {
  number:45, status:125, recommendation:280, dimension:140, severity:90,
  complexity:115, userDisruption:145, notes:65, session:100, support:80, age:70,
};
const recsTableWidth = (visibleIds, bulkMode, compact) =>
  visibleIds.reduce((sum, id) => sum + (compact && (id === "complexity" || id === "userDisruption")
    ? 58 : RECS_COLUMN_WIDE_WIDTH[id]), bulkMode ? 44 : 0);
const RECS_COLUMN_DEFAULTS = {
  order: RECS_COLUMN_IDS,
  visible: RECS_COLUMN_DEFINITIONS.filter(column => column.defaultVisible).map(column => column.id),
};

const normalizeRecsColumnPreference = (value) => {
  if (!value || value.version !== 1 || !Array.isArray(value.order) || !Array.isArray(value.visible)) return null;
  const order = value.order;
  const visible = value.visible;
  if (order.length !== RECS_COLUMN_IDS.length
    || new Set(order).size !== RECS_COLUMN_IDS.length
    || !RECS_COLUMN_IDS.every(id => order.includes(id))) return null;
  if (visible.some(id => !RECS_COLUMN_IDS.includes(id)) || new Set(visible).size !== visible.length) return null;
  const requiredIds = RECS_COLUMN_DEFINITIONS.filter(column => column.required).map(column => column.id);
  if (!requiredIds.every(id => visible.includes(id))) return null;
  const requiredOrder = order.filter(id => requiredIds.includes(id));
  if (requiredOrder.some((id, index) => id !== requiredIds[index])) return null;
  return { order, visible };
};

export const RecsPage = ({ reviewCompleteControl = null, columnPreferenceAdapter, viewPreferenceAdapter, searchMaxLength, filter: initialFilter, catFilter: initialCatFilter, categoryFilter: initialCategoryFilter, overrideFilter: initialOverrideFilter, regressedFilter: initialRegressedFilter, exactSelection = null, onExactSelectionChange, dashboardHeaderHeight = 0, onFilterChange, onCatFilterChange, onCategoryFilterChange, onOverrideFilterChange, onRegressedFilterChange, highlightId, onSupport, onOpenPanel, settingDetails, onSavePanel, onAddToQueue, onRemoveFromQueue, queueIds, queueCount, onNavQueue, recs, notesBySettingId, sessionFocus, sessionFocusError, sessionFocusBanner, onDismissSessionFocusBanner, sessionFocusPanelState, onSessionFocusPanelStateChange, onSessionFocusItemClick, onRemoveSessionFocusItem, onUpdateSessionFocusItem, onReorderSessionFocus, reviewsBySettingId, onMarkReviewed, reviewBanner, onDismissReviewBanner, currentUserEmail, reportLoading, reportError, noDataScanState = null, onNavProfile, pendingReview = new Set(), pendingQueue = new Set(), isPatriot = false, bulkIntent = false, onBulkIntentConsumed, slug, clientName = "", deliveryMode = false }) => {
  const [initialView] = useState(() => viewPreferenceAdapter?.read?.() || {});
  // Real notes count from backend-backed Map. Wrapped so callers can stay
  // forgiving if notesBySettingId is ever undefined (it always is today).
  const realNotesCount = (settingId) => (notesBySettingId?.get(settingId) || []).length;
  // True if a setting has at least one Short-term note whose revisit_by is in the past.
  const hasPastRevisit = (settingId) => {
    const notes = notesBySettingId?.get(settingId) || [];
    return notes.some(n => {
      if (!n.revisit_by) return false;
      const lifecycle = ((n.lifecycle || "") + "").toLowerCase();
      const isShortTerm = lifecycle === "short-term" || lifecycle === "short_term" || lifecycle === "shortterm";
      return isShortTerm && isStale(n.revisit_by);
    });
  };
  // Count of settings (not notes) with at least one past-revisit note — drives the chip badge.
  // Computed against all recs (not the filtered list) so the badge is stable as other filters toggle.
  const pastRevisitCount = recs.filter(r => hasPastRevisit(r.id)).length;
  // Count of regressed settings across all recs (stable at-a-glance signal,
  // independent of other active filters) — drives the Regressed toggle badge.
  const regressedCount = recs.filter(r => r.regressed).length;
  const [filter, setFilter] = useState(initialView.filter ?? initialFilter ?? "all");
  const [catFilter, setCatFilter] = useState(initialView.catFilter ?? initialCatFilter ?? null);
  const [categoryFilter, setCategoryFilter] = useState(initialView.categoryFilter ?? initialCategoryFilter ?? null);  // Security Domain (r.category)
  const [expanded, setExpanded] = useState(new Set(highlightId ? [highlightId] : []));
  const highlightRef = useRef(null);
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkSel, setBulkSel] = useState(new Set());
  const [hasNotesFilter, setHasNotesFilter] = useState(initialView.hasNotesFilter ?? false);
  const [pastRevisitFilter, setPastRevisitFilter] = useState(initialView.pastRevisitFilter ?? false);
  const [showSatisfied, setShowSatisfied] = useState(initialView.showSatisfied ?? !!exactSelection);
  // Not Assessed row-class visibility (2026-07-28 decision): hidden by
  // default alongside Satisfied, surfaced through the shared "Hidden" menu.
  // Session-persistence only (React state) — deliberately no localStorage.
  const [showNotAssessed, setShowNotAssessed] = useState(initialView.showNotAssessed ?? !!exactSelection);
  const [hiddenMenuOpen, setHiddenMenuOpen] = useState(false);
  // Phase E2 — Override status filter. Lifted to parent (recOverrideFilter)
  // so navToRecs can set it from donut clicks. Mirrors initialFilter/initialCatFilter pattern.
  const [overrideFilter, setOverrideFilter] = useState(initialView.overrideFilter ?? initialOverrideFilter ?? null);
  // Column-header sorting (view-local; not lifted). sortColumn=null → default order.
  const [sortColumn, setSortColumn] = useState(initialView.sortColumn ?? null);
  const [sortDir, setSortDir] = useState(initialView.sortDir ?? "asc");
  const [colDim, setColDim] = useState(initialView.colDim ?? "product");  // recs-table column dimension: product | category | license
  const [columnPreference, setColumnPreference] = useState(() => normalizeRecsColumnPreference(columnPreferenceAdapter?.read?.()) || RECS_COLUMN_DEFAULTS);
  const [columnsMenuOpen, setColumnsMenuOpen] = useState(false);
  const [compactRatingColumns, setCompactRatingColumns] = useState(false);
  const [tableScroll, setTableScroll] = useState({ overflow:false, left:false, right:false });
  const [ratingDisclosure, setRatingDisclosure] = useState(null);
  const [ratingDisclosurePosition, setRatingDisclosurePosition] = useState(null);
  const ratingDisclosureAnchorRef = useRef(null);
  const tableViewportRef = useRef(null);
  const visibleColumnIds = columnPreference.order.filter(id => columnPreference.visible.includes(id));
  const visibleColumnKey = visibleColumnIds.join(",");
  const wideTableWidth = recsTableWidth(visibleColumnIds, bulkMode, false);
  const tableMinWidth = recsTableWidth(visibleColumnIds, bulkMode, compactRatingColumns);
  const denseTable = visibleColumnIds.length + (bulkMode ? 1 : 0) >= 9;
  const syncTableScroll = () => {
    const node = tableViewportRef.current;
    if (!node) return;
    const max = Math.max(0, node.scrollWidth - node.clientWidth);
    if (node.scrollLeft > max) node.scrollLeft = max;
    const position = Math.max(0, node.scrollLeft);
    const next = { overflow:max > 1, left:position > 1, right:position < max - 1 };
    setTableScroll(current => current.overflow === next.overflow && current.left === next.left && current.right === next.right ? current : next);
  };
  const scrollTable = (direction) => {
    const node = tableViewportRef.current;
    if (!node) return;
    node.scrollLeft = Math.max(0, Math.min(node.scrollWidth - node.clientWidth,
      node.scrollLeft + direction * Math.max(160, node.clientWidth * .75)));
    syncTableScroll();
  };
  const setColumnVisible = (id, visible) => {
    const definition = RECS_COLUMN_DEFINITIONS.find(column => column.id === id);
    if (!definition || definition.required) return;
    setColumnPreference(current => {
      const nextVisible = visible
        ? [...current.visible, id]
        : current.visible.filter(columnId => columnId !== id);
      return { ...current, visible: nextVisible };
    });
    if (!visible && definition.sort && sortColumn === definition.sort) {
      setSortColumn(null);
      setSortDir("asc");
    }
  };
  const moveColumn = (id, direction) => setColumnPreference(current => {
    const index = current.order.indexOf(id);
    const nextIndex = index + direction;
    if (index < 0 || nextIndex < 0 || nextIndex >= current.order.length) return current;
    const order = [...current.order];
    [order[index], order[nextIndex]] = [order[nextIndex], order[index]];
    return { ...current, order };
  });
  const resetColumns = () => {
    setColumnPreference({ order:[...RECS_COLUMN_DEFAULTS.order], visible:[...RECS_COLUMN_DEFAULTS.visible] });
    setColumnsMenuOpen(false);
  };
  useEffect(() => { columnPreferenceAdapter?.write?.({ version:1, ...columnPreference }); }, [columnPreference, columnPreferenceAdapter]);
  useEffect(() => {
    const node = tableViewportRef.current;
    if (!node) return undefined;
    const updateWidth = () => {
      setCompactRatingColumns(node.clientWidth > 0 && wideTableWidth > node.clientWidth);
      syncTableScroll();
    };
    updateWidth();
    window.addEventListener("resize", updateWidth);
    let observer;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(updateWidth);
      observer.observe(node);
      observer.observe(node.querySelector("table"));
    }
    return () => {
      window.removeEventListener("resize", updateWidth);
      observer?.disconnect();
    };
  }, [reportLoading, reportError, visibleColumnKey, bulkMode, wideTableWidth]);
  useEffect(() => { syncTableScroll(); }, [visibleColumnKey, bulkMode, compactRatingColumns, tableMinWidth]);
  useEffect(() => {
    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        setColumnsMenuOpen(false);
        setRatingDisclosure(null);
      }
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, []);
  useEffect(() => {
    if (!ratingDisclosure) {
      setRatingDisclosurePosition(null);
      ratingDisclosureAnchorRef.current = null;
      return undefined;
    }
    const updatePosition = () => {
      const anchor = ratingDisclosureAnchorRef.current;
      if (!anchor?.isConnected) {
        setRatingDisclosure(null);
        return;
      }
      const rect = anchor.getBoundingClientRect();
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
      // Reserve room for the full label before clamping its left edge. The
      // portal itself uses max-content, so short labels stay compact while
      // longer values wrap within the same viewport-safe width.
      const tooltipWidth = Math.max(0, Math.min(260, viewportWidth - 16));
      const left = Math.max(8, Math.min(rect.left, viewportWidth - tooltipWidth - 8));
      const tooltipHeight = 30;
      const top = rect.top >= tooltipHeight + 13
        ? rect.top - tooltipHeight - 5
        : Math.max(8, Math.min(rect.bottom + 5, viewportHeight - tooltipHeight - 8));
      setRatingDisclosurePosition({ left, top, maxWidth:tooltipWidth });
    };
    updatePosition();
    document.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    window.visualViewport?.addEventListener("resize", updatePosition);
    window.visualViewport?.addEventListener("scroll", updatePosition);
    return () => {
      document.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
      window.visualViewport?.removeEventListener("resize", updatePosition);
      window.visualViewport?.removeEventListener("scroll", updatePosition);
    };
  }, [ratingDisclosure]);
  // Cycle a column: off → asc → desc → off (back to default order).
  const sortBy = (colId) => {
    if (colId !== sortColumn) { setSortColumn(colId); setSortDir("asc"); }
    else if (sortDir === "asc") { setSortDir("desc"); }
    else { setSortColumn(null); setSortDir("asc"); }
  };
  // New consolidated filter-bar state. `severityFilter`/`statusFilter` are the
  // source of truth for severity/status; the legacy single-select `filter` is
  // kept only as the deep-link inbound channel (dashboard MetricCards →
  // navToRecs → recFilter → initialFilter), bridged into the new state below.
  const [statusFilter, setStatusFilter] = useState(initialView.statusFilter ?? []);     // effective-status keys; empty = all
  const [severityFilter, setSeverityFilter] = useState(initialView.severityFilter ?? []); // sev values; empty = all
  const [search, setSearch] = useState(initialView.search ?? "");
  const [includeNotesInSearch, setIncludeNotesInSearch] = useState(initialView.includeNotesInSearch ?? true);
  const [regressedFilter, setRegressedFilter] = useState(initialView.regressedFilter ?? !!initialRegressedFilter);
  // Quick Wins deep-link (Posture Movement tile) — one composite chip covering
  // the tile's exact population; no generic difficulty/impact filter UI exists.
  const [quickWinsFilter, setQuickWinsFilter] = useState(initialView.quickWinsFilter ?? initialFilter === "quick_wins");
  const [difficultyFilter, setDifficultyFilter] = useState(initialView.difficultyFilter ?? []);
  const [impactFilter, setImpactFilter] = useState(initialView.impactFilter ?? []);
  const [ratingMenu, setRatingMenu] = useState(null);
  useEffect(() => { viewPreferenceAdapter?.write?.({
    filter, catFilter, categoryFilter, overrideFilter, hasNotesFilter, pastRevisitFilter,
    showSatisfied, showNotAssessed, sortColumn, sortDir, colDim, statusFilter,
    severityFilter, search, includeNotesInSearch, regressedFilter, quickWinsFilter,
    difficultyFilter, impactFilter,
  }); }, [viewPreferenceAdapter, filter, catFilter, categoryFilter, overrideFilter,
    hasNotesFilter, pastRevisitFilter, showSatisfied, showNotAssessed, sortColumn,
    sortDir, colDim, statusFilter, severityFilter, search, includeNotesInSearch,
    regressedFilter, quickWinsFilter, difficultyFilter, impactFilter]);
  useEffect(() => {
    const closeOnEscape = (event) => { if (event.key === "Escape") setRatingMenu(null); };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, []);
  // Needs Action — the disposition half of the "Critical + High Open" tile
  // deep-link and the top severity cards' per-severity "+N more" deep-links
  // (severity arrives via severityFilter): base-status open AND the shared
  // tileDisposition classifier says the row still needs action (the same
  // call the tile and the top severity cards make).
  // INVARIANT: A TILE'S NUMBER MUST EQUAL THE "Showing N" COUNT ITS CLICK
  // LANDS ON — this predicate must mirror the tile's crHiOpen math exactly.
  // Deliberately NOT overrideFilter="unresolved_gap": that filter is
  // earned-points-based (earned !== max_possible) and would leak "partial"
  // rows the tile's status === "open" math never counts.
  const [needsActionFilter, setNeedsActionFilter] = useState(
    initialFilter === "critical_high" || !!NEEDS_ACTION_SEV_KEYS[initialFilter]
  );
  const [statusMenuOpen, setStatusMenuOpen] = useState(false); // Status multi-select dropdown
  const [catMenuOpen, setCatMenuOpen] = useState(false);        // Category single-select dropdown
  // Reset every filter to its default (no restriction). Includes overrideFilter
  // and pastRevisitFilter — both can be active from deep-links / prior state even
  // though the new bar has no dedicated control for them.
  const clearAllFilters = () => {
    setSearch("");
    setSeverityFilter([]);
    setStatusFilter([]);
    setCatFilter(null); onCatFilterChange && onCatFilterChange(null);
    setCategoryFilter(null); onCategoryFilterChange && onCategoryFilterChange(null);
    setHasNotesFilter(false);
    setRegressedFilter(false); onRegressedFilterChange && onRegressedFilterChange(false);
    setQuickWinsFilter(false);
    setDifficultyFilter([]);
    setImpactFilter([]);
    setNeedsActionFilter(false);
    setShowSatisfied(false);
    setShowNotAssessed(false);
    setPastRevisitFilter(false);
    setOverrideFilter(null); onOverrideFilterChange && onOverrideFilterChange(null);
    setFilter("all"); onFilterChange && onFilterChange("all");
    onExactSelectionChange && onExactSelectionChange(null);
  };

  useEffect(() => { if (initialFilter) setFilter(initialFilter); }, [initialFilter]);
  // Bridge inbound deep-links (dashboard MetricCards) into the new multi-select
  // state. A severity key seeds severityFilter; "implemented" is a status, so it
  // seeds statusFilter + reveals satisfied rows. "all"/"new" clear the bridge.
  useEffect(() => {
    if (!initialFilter) return;
    const SEV_KEYS = ["critical", "high", "medium", "low", "informational"];
    if (initialFilter === "governance") {
      setSeverityFilter(["informational"]);
    } else if (initialFilter === "critical_high") {
      // Posture Movement "Critical + High Open" tile — composite deep-link
      // (two internal states, quick_wins-style): both severities at once PLUS
      // the tile's disposition (open, no credit override) via needsActionFilter,
      // so the landed "Showing N" equals the tile's number.
      setSeverityFilter(["critical", "high"]);
      setNeedsActionFilter(true);
    } else if (NEEDS_ACTION_SEV_KEYS[initialFilter]) {
      // Top severity cards' "+N more →" — one severity + the needs-action
      // disposition, so the card headline equals the landed "Showing N".
      // The bare severity keys in SEV_KEYS below stay raw for other callers.
      setSeverityFilter([NEEDS_ACTION_SEV_KEYS[initialFilter]]);
      setNeedsActionFilter(true);
    } else if (initialFilter === "quick_wins") {
      // Posture Movement "Quick Wins" tile — composite filter, not a severity seed.
      setQuickWinsFilter(true);
      setSeverityFilter([]);
    } else if (SEV_KEYS.includes(initialFilter)) {
      setSeverityFilter([initialFilter]);
    } else if (initialFilter === "implemented") {
      setStatusFilter(["implemented"]);
      setShowSatisfied(true);
    } else {
      // "all" (or unknown) — no inbound restriction.
      setSeverityFilter([]);
    }
  }, [initialFilter]);
  useEffect(() => { if (initialCatFilter !== undefined) setCatFilter(initialCatFilter); }, [initialCatFilter]);
  useEffect(() => { if (initialCategoryFilter !== undefined) setCategoryFilter(initialCategoryFilter); }, [initialCategoryFilter]);
  useEffect(() => {
    if (initialOverrideFilter !== undefined) setOverrideFilter(initialOverrideFilter);
    // "any_credit" deep-link (Risk Decisions tile): reveal satisfied rows too —
    // a satisfied-but-overridden setting is still a recorded risk decision, and
    // hiding it would make the landed "Showing N" undercount the tile.
    if (initialOverrideFilter === "any_credit") setShowSatisfied(true);
  }, [initialOverrideFilter]);
  useEffect(() => { if (initialRegressedFilter !== undefined) setRegressedFilter(!!initialRegressedFilter); }, [initialRegressedFilter]);
  useEffect(() => {
    if (highlightId) {
      setExpanded(new Set([highlightId]));
      setTimeout(() => highlightRef.current?.scrollIntoView({ behavior:"smooth", block:"center" }), 100);
    }
  }, [highlightId]);
  // Cross-page intent from the dashboard "Settings help" action: open already in
  // bulk-select mode with an empty selection, then clear the flag so it fires once.
  useEffect(() => {
    if (bulkIntent) {
      setBulkMode(true);
      setBulkSel(new Set());
      onBulkIntentConsumed?.();
    }
  }, [bulkIntent, onBulkIntentConsumed]);

  const enterBulk = (rec) => {
    setBulkMode(true);
    setBulkSel(new Set([rec.id]));
  };
  const exitBulk = () => { setBulkMode(false); setBulkSel(new Set()); };
  const toggleBulkItem = (id) => setBulkSel(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  const submitBulk = () => {
    const bulkRecs = recs.filter(r => bulkSel.has(r.id));
    if (bulkRecs.length) onSupport(bulkRecs);
    exitBulk();
  };

  let list = recs;
  // Exact Failed-to-Passed membership is an additive AND constraint. It is
  // applied before the ordinary pipeline so every later user filter narrows
  // the landed population rather than replacing it.
  if (exactSelection) {
    list = list.filter(r => exactSelection.ids.has(r.id));
  }
  if (catFilter) {
    const mc = PRODUCT_CAT_MAP[catFilter] || [catFilter];
    list = list.filter(r => mc.includes(r.cat));
  }
  if (categoryFilter) {
    // Security Domain (r.category) filter. "Unclassified" is a bars-only display
    // bucket — no setting literally has category === "Unclassified" — so it maps
    // to settings with NO category, round-tripping the bar label correctly.
    list = categoryFilter === "Unclassified"
      ? list.filter(r => !r.category)
      : list.filter(r => r.category === categoryFilter);
  }
  // (Legacy single-select severity removed — severity is now the multi-select
  // `severityFilter` applied further down. `filter` survives only as the
  // deep-link inbound channel, bridged into severityFilter/statusFilter above.)
  if (hasNotesFilter) list = list.filter(r => realNotesCount(r.id) > 0);
  if (regressedFilter) list = list.filter(r => r.regressed);
  // Mirrors PostureMovement's isQuickWin exactly: open, low-effort, low-disruption,
  // Critical/High — so the tile's headline count and this list always agree.
  if (quickWinsFilter) list = list.filter(r => r.status === "open" && isQuickWinRating(r) && (r.sev === "critical" || r.sev === "high"));
  const ratingValue = (value) => (typeof value === "string" && value.trim() ? value : "Not rated");
  if (difficultyFilter.length) list = list.filter(r => difficultyFilter.includes(ratingValue(complexityForDisplay(r))));
  if (impactFilter.length) list = list.filter(r => impactFilter.includes(ratingValue(r.user_impact)));
  // Mirrors the tiles' disposition math exactly — same tileDisposition
  // classifier the severity cards and crHiOpen call — see the tile-site
  // invariant. Note it excludes never-assessed rows on its own rather than
  // leaning on the showNotAssessed default below: this list is what a tile
  // click lands on, so its count must equal the tile whatever the Hidden
  // menu is set to.
  if (needsActionFilter) list = list.filter(r => r.status === "open" && countsAsOpen(r.verdict, settingDetails?.[r.id]?.override));
  if (pastRevisitFilter) list = list.filter(r => hasPastRevisit(r.id));
  if (!showSatisfied) list = list.filter(r => r.status !== "implemented");
  // Not Assessed class hidden by default (2026-07-28 decision) — these rows
  // earn nothing and count nothing; they surface via the Hidden menu.
  if (!showNotAssessed) list = list.filter(r => r.verdict !== "not_assessed");
  // Phase E2 — Override status filter. Applied last so it sees the post-other-filters
  // list and respects severity/category/notes/satisfied scoping.
  if (overrideFilter) {
    if (overrideFilter === "verified") {
      list = list.filter(r => r.max_possible > 0 && r.earned === r.max_possible);
    } else if (overrideFilter === "unresolved_gap") {
      // Still failing AND no credit-granting override applied
      list = list.filter(r => {
        if (r.earned === r.max_possible) return false; // fully passing — not a gap
        const ov = settingDetails?.[r.id]?.override;
        return ov !== "risk_accepted" && ov !== "customer_managed_solution" && ov !== "customer_remediated";
      });
    } else if (overrideFilter === "any_credit") {
      // Posture Movement "Risk Decisions Recorded" tile — any credit-granting
      // override on an assessed row. Same classifier as the tile, and it
      // excludes never-assessed rows itself rather than leaning on the
      // showNotAssessed default, so the landed count equals the tile whatever
      // the Hidden menu is set to (#532 rule).
      list = list.filter(r => countsAsAddressed(r.verdict, settingDetails?.[r.id]?.override));
    } else {
      // risk_accepted / customer_managed_solution / customer_remediated
      list = list.filter(r => settingDetails?.[r.id]?.override === overrideFilter);
    }
  }

  // ── New consolidated-bar filters (3b-1). Compose via AND with the chain above;
  //    empty selection / blank search = no restriction. Applied in bulk mode too
  //    (walkthrough, July 7 — reverses the 3b-1 "full rec list in bulk" gate,
  //    which the deep-link chain above never honored anyway): filters affect
  //    visibility only; submitBulk reads the full recs array by selected id. ──
  // (a) Severity multi-select. "informational" also matches legacy "governance".
  if (severityFilter.length > 0) {
    const sevSet = new Set(severityFilter);
    list = list.filter(r => sevSet.has(r.sev)
      || (sevSet.has("informational") && r.sev === "governance"));
  }

  // (b) Status multi-select by EFFECTIVE status key (override wins, via helper).
  //     Note: effectiveStatusKey collapses customer_remediated → "pending", so
  //     selecting "pending" includes both. Acceptable for now.
  if (statusFilter.length > 0) {
    const statusSet = new Set(statusFilter);
    // Not Assessed rows classify as "not_assessed" here (never "open") so the
    // Open selection doesn't sweep in rows that were never evaluated.
    list = list.filter(r => {
      const key = r.verdict === "not_assessed" ? "not_assessed" : effectiveStatusKey(r, settingDetails);
      return statusSet.has(key);
    });
  }

  // (c) Free-text search across id / name / recommended value / recommendation /
  //     result text, and optionally note bodies.
  if (search.trim()) {
    const q = search.trim().toLowerCase();
    list = list.filter(r =>
      String(r.id).includes(q)
      || (r.name || "").toLowerCase().includes(q)
      || (r.recommended_value || "").toLowerCase().includes(q)
      || (r.audit_recommendation || "").toLowerCase().includes(q)
      || (r.result_text || "").toLowerCase().includes(q)
      || (includeNotesInSearch
          && (notesBySettingId?.get(r.id) || []).some(n => ((n.body || n.text) || "").toLowerCase().includes(q)))
    );
  }

  // Did this row match the search specifically because of a note? Drives the
  // "matched in note" indicator in the UI (3b-2).
  const matchedInNote = (r) => {
    const q = search.trim().toLowerCase();
    if (!q || !includeNotesInSearch) return false;
    return (notesBySettingId?.get(r.id) || []).some(n => ((n.body || n.text) || "").toLowerCase().includes(q));
  };

  // Default order: severity rank → category (A→Z) → setting number ascending.
  // Also used as the stable tiebreaker for header-column sorts. (SEV_SORT_RANK
  // is module-scope so the comparator and the default share it.)
  const defaultCompare = (a, b) => {
    const ra = SEV_SORT_RANK[a.sev] ?? 99;
    const rb = SEV_SORT_RANK[b.sev] ?? 99;
    if (ra !== rb) return ra - rb;
    const ca = (a.cat || "").localeCompare(b.cat || "");
    if (ca !== 0) return ca;
    return a.id - b.id;
  };

  // Per-column primary comparison. Age: missing/invalid dates sort LAST in both
  // directions (handled before the dir flip). Returns a number; 0 = defer to tiebreaker.
  const ageTime = (r) => {
    if (!r.first_identified) return null;
    const t = new Date(r.first_identified).getTime();
    return Number.isNaN(t) ? null : t;
  };
  const primaryCompare = (col, a, b) => {
    if (col === "num")    return a.id - b.id;
    if (col === "status") return (STATUS_SORT_RANK[effectiveStatusKey(a, settingDetails)] ?? 99)
                               - (STATUS_SORT_RANK[effectiveStatusKey(b, settingDetails)] ?? 99);
    if (col === "rec")    return (a.name || "").localeCompare(b.name || "");
    if (col === "cat") { const k = colDim === "category" ? "category" : colDim === "license" ? "license" : "cat"; return ((a[k] || "") + "").localeCompare((b[k] || "") + ""); }
    if (col === "sev")    return (SEV_SORT_RANK[a.sev] ?? 99) - (SEV_SORT_RANK[b.sev] ?? 99);
    if (col === "age") {
      const ta = ageTime(a), tb = ageTime(b);
      if (ta === null && tb === null) return 0;
      if (ta === null) return 1;   // a missing → after b
      if (tb === null) return -1;  // b missing → after a
      return ta - tb;              // "asc" = oldest-first (earliest date first)
    }
    return 0;
  };

  // Copy rather than mutate — `list` may still reference the recs prop here.
  if (!sortColumn) {
    list = [...list].sort(defaultCompare);
  } else {
    const dirMul = sortDir === "desc" ? -1 : 1;
    list = [...list].sort((a, b) => {
      // Missing-date rows always sort last, independent of direction.
      if (sortColumn === "age") {
        const ta = ageTime(a), tb = ageTime(b);
        if (ta === null && tb !== null) return 1;
        if (tb === null && ta !== null) return -1;
      }
      const primary = primaryCompare(sortColumn, a, b);
      if (primary !== 0) return primary * dirMul;
      return defaultCompare(a, b); // stable tiebreaker, never flipped
    });
  }

  const thStyle = { padding:"10px 8px", borderBottom:`1px solid ${T.border}`, textAlign:"left", fontSize:12, color:T.muted, fontWeight:600, textTransform:"uppercase", letterSpacing:".04em" };
  // Sortable-header affordances: clickable cursor + sort indicator. Inactive
  // sortable columns show a faint ⇅ to signal they're clickable; the active
  // column shows a bright ▲/▼. (Logic in sortBy is unchanged.)
  const sortableHdr = { cursor:"pointer", userSelect:"none" };
  const sortArrow = (col) => (
    sortColumn === col
      ? <span style={{ marginLeft:4, color:T.text }}>{sortDir === "asc" ? "▲" : "▼"}</span>
      : <span style={{ marginLeft:4, color:T.muted, opacity:0.35 }}>⇅</span>
  );
  const columnMoveButtonStyle = {
    width:25, height:25, borderRadius:5, border:`1px solid ${T.border}`,
    background:"rgba(255,255,255,.04)", color:T.text, cursor:"pointer", fontSize:17, lineHeight:1,
  };
  const ratingIcon = (columnId) => columnId === "complexity"
    ? <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="m12 3 8 4-8 4-8-4 8-4Z"/><path d="m4 12 8 4 8-4M4 17l8 4 8-4"/></svg>
    : <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="8" r="3"/><path d="M3 20v-1a6 6 0 0 1 12 0v1M16 5a3 3 0 0 1 0 6M18 14a5 5 0 0 1 3 4v2"/></svg>;
  const ratingDisplay = (columnId, rating, settingId) => {
    const category = columnId === "complexity" ? "Complexity" : "User disruption";
    const value = typeof rating === "string" && rating.trim() ? rating.trim() : "Not rated";
    const compactKnown = { Low:"L", Medium:"M", High:"H", "Not rated":"?" };
    const compactValue = value === "None" ? "None" : compactKnown[value];
    const knownValue = value === "Low" || value === "Medium" || value === "High" || value === "None" || value === "Not rated";
    const accessibleLabel = `${category}: ${value}`;
    const ratingColor = value === "Low" ? "#8bc99e" : value === "High" ? "#deb879" : value === "Not rated" ? T.muted : T.text;
    if (!compactRatingColumns) return <span style={{ color:ratingColor }}>{value}</span>;
    const disclosureId = `${settingId}:${columnId}`;
    const revealed = ratingDisclosure === disclosureId;
    const reveal = (event) => {
      ratingDisclosureAnchorRef.current = event.currentTarget;
      setRatingDisclosure(disclosureId);
    };
    return <span style={{ position:"relative", display:"inline-flex" }}>
      <button
        type="button"
        aria-label={accessibleLabel}
        aria-expanded={revealed}
        aria-describedby={revealed ? "rating-disclosure-tooltip" : undefined}
        title={accessibleLabel}
        onClick={reveal}
        onFocus={reveal}
        onBlur={() => setRatingDisclosure(current => current === disclosureId ? null : current)}
        onMouseEnter={reveal}
        onMouseLeave={() => setRatingDisclosure(current => current === disclosureId ? null : current)}
        style={{ display:"inline-flex", alignItems:"center", justifyContent:"flex-start", gap:5, padding:0, border:0, background:"none", color:T.text, fontFamily:"inherit", fontSize:12, cursor:"help", whiteSpace:"nowrap" }}
      >
        <span style={{ display:"inline-flex", color:T.muted }}>{ratingIcon(columnId)}</span>
        <span style={{ color:ratingColor }}>{knownValue ? compactValue : value}</span>
      </button>
      {revealed && ratingDisclosurePosition && createPortal(
        <span id="rating-disclosure-tooltip" role="tooltip" style={{
          position:"fixed", zIndex:10000, left:ratingDisclosurePosition.left, top:ratingDisclosurePosition.top,
          maxWidth:ratingDisclosurePosition.maxWidth, padding:"5px 7px", borderRadius:5,
          border:`1px solid ${T.border}`, background:T.panel, color:T.text, fontSize:11,
          whiteSpace:"normal", overflowWrap:"anywhere", boxShadow:"0 4px 12px rgba(0,0,0,.3)",
        }}>{accessibleLabel}</span>, document.body)}
    </span>;
  };

  // ── New consolidated filter bar (3b-2) ──
  // Severity segments (segmented multi-select). Colors match the dashboard dots.
  const SEVERITY_SEGMENTS = [
    { value:"critical",      label:"Critical",      color:"#8B5CF6" },
    { value:"high",          label:"High",          color:"#EF4444" },
    { value:"medium",        label:"Medium",        color:"#F97316" },
    { value:"low",           label:"Low",           color:"#FACC15" },
    { value:"informational", label:"Informational", color:"#475569" },
  ];
  // Status options (multi-select dropdown). Values are EFFECTIVE-status keys.
  // Customer Remediated collapses into Pending via effectiveStatusKey — not listed.
  const STATUS_OPTIONS = [
    { value:"open",             label:"Open" },
    { value:"partial",          label:"Partial" },
    { value:"pending",          label:"Pending" },
    { value:"planned",          label:"Planned" },
    { value:"exception",        label:"Risk Accepted" },
    { value:"managed_solution", label:"Customer Managed" },
    { value:"implemented",      label:"Satisfied" },
    { value:"not_assessed",     label:"Not Assessed" },
  ];
  const STATUS_LABEL = Object.fromEntries(STATUS_OPTIONS.map(o => [o.value, o.label]));
  const SEVERITY_LABEL = Object.fromEntries(SEVERITY_SEGMENTS.map(o => [o.value, o.label]));
  const toggleInArray = (setFn, val) => setFn(prev => prev.includes(val) ? prev.filter(x => x !== val) : [...prev, val]);
  const toggleSeverity = (val) => toggleInArray(setSeverityFilter, val);
  const toggleStatus = (val) => {
    // Selecting the Not Assessed status auto-reveals the hidden class —
    // never leave the user filtering for rows that can't render.
    if (val === "not_assessed" && !statusFilter.includes(val)) setShowNotAssessed(true);
    toggleInArray(setStatusFilter, val);
  };
  const hasRatingFilters = difficultyFilter.length > 0 || impactFilter.length > 0;
  const anyFilterActive = !!(exactSelection || search.trim() || severityFilter.length || statusFilter.length || hasRatingFilters || catFilter || categoryFilter || hasNotesFilter || regressedFilter || quickWinsFilter || needsActionFilter || showSatisfied || showNotAssessed || pastRevisitFilter || overrideFilter);
  const OVERRIDE_CHIP_LABEL = {
    verified: "Patriot Verified", risk_accepted: "Risk Accepted",
    customer_managed_solution: "Customer Managed Solution",
    customer_remediated: "Customer Remediated", unresolved_gap: "Open Gap",
    any_credit: "Risk Decisions",
  };

  // Shared pill/toggle style factory for the bar.
  const barToggle = (active, accent = "rgba(68,166,255,") => ({
    padding:"6px 10px", borderRadius:8, border:`1px solid ${active ? accent + ".5)" : T.border}`,
    background: active ? accent + ".15)" : "rgba(255,255,255,.04)",
    color:T.text, cursor:"pointer", fontFamily:"inherit", fontSize:12, transition:".15s",
    display:"inline-flex", alignItems:"center", gap:6, whiteSpace:"nowrap",
  });
  // #RRGGBB → rgba() at the given alpha (for per-severity active tints).
  const hexA = (hex, a) => {
    const h = hex.replace("#", "");
    const n = parseInt(h, 16);
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
  };

  const colCount = visibleColumnIds.length + (bulkMode ? 1 : 0);

  if (reportLoading) {
    return <SkeletonRecsTable />;
  }
  if (reportError === "roster_inactive") {
    return <EmptyState title={ROSTER_INACTIVE_TITLE} message={ROSTER_INACTIVE_MESSAGE} icon="🔒" />;
  }
  if (reportError === "no_data") {
    return <NoDataEmptyState scanState={noDataScanState} onGoToProfile={onNavProfile} />;
  }

  return (
    <div>
      {/* ── Filter bar / Bulk mode bar (sticky beneath the dashboard header) ── */}
      <div style={{ position:"sticky", top: dashboardHeaderHeight, zIndex:49, background:"#08182e", paddingTop:6, paddingBottom:10, marginBottom:0 }}>
      {bulkMode && (() => {
        // Selected ids absent from the filtered list — still submitted (submitBulk
        // reads the full recs array), so say they're hidden rather than lost.
        const visibleIds = new Set(list.map(r => r.id));
        const hiddenSel = [...bulkSel].filter(id => !visibleIds.has(id)).length;
        return (
        <div style={{ display:"flex", gap:8, alignItems:"center", padding:"10px 14px", marginBottom:8, background:"rgba(68,166,255,.08)", border:"1px solid rgba(68,166,255,.25)", borderRadius:10 }}>
          <span style={{ fontSize:13, color:T.blue, fontWeight:600, flex:1 }}>Select items to include in support ticket</span>
          <span style={{ fontSize:12, color:T.text, fontWeight:700 }}>{bulkSel.size} item{bulkSel.size !== 1 ? "s" : ""} selected{hiddenSel > 0 ? ` (${hiddenSel} hidden by filters)` : ""}</span>
          <button onClick={submitBulk} style={{
            padding:"6px 14px", borderRadius:8, border:"1px solid rgba(68,166,255,.4)", background:"rgba(68,166,255,.2)",
            color:T.blue, cursor: bulkSel.size ? "pointer" : "default", fontFamily:"inherit", fontSize:12, fontWeight:700,
            opacity: bulkSel.size ? 1 : 0.5,
          }}>Create Ticket</button>
          <button onClick={exitBulk} style={{
            padding:"6px 14px", borderRadius:8, border:`1px solid ${T.border}`, background:"rgba(255,255,255,.04)",
            color:T.muted, cursor:"pointer", fontFamily:"inherit", fontSize:12, fontWeight:600,
          }}>Cancel</button>
        </div>
        );
      })()}
      {/* Filter bar — always rendered; in bulk mode it narrows the selectable list. */}
        <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"center" }}>
          {/* 1) Search box with its own "Include notes" option (a search setting,
              not a peer filter) attached directly beneath the input. */}
          <div style={{ display:"inline-flex", flexDirection:"column", gap:3, alignSelf:"flex-start" }}>
            <div style={{ position:"relative", display:"inline-flex", alignItems:"center" }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.muted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ position:"absolute", left:10, pointerEvents:"none" }}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              <input
                type="text"
                value={search}
                maxLength={searchMaxLength}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search settings, recommendations, notes…"
                style={{
                  padding:"6px 28px 6px 30px", borderRadius:8, width:240, maxWidth:"48vw",
                  border:`1px solid ${search ? "rgba(68,166,255,.5)" : T.border}`,
                  background:"rgba(255,255,255,.04)", color:T.text, fontFamily:"inherit", fontSize:12, outline:"none",
                }}
              />
              {search && (
                <button onClick={() => setSearch("")} aria-label="Clear search" style={{
                  position:"absolute", right:6, background:"none", border:"none", color:T.muted,
                  cursor:"pointer", fontSize:14, lineHeight:1, fontFamily:"inherit", padding:2,
                }}>×</button>
              )}
            </div>
            <label title="Include note text when searching" style={{
              display:"inline-flex", alignItems:"center", gap:5, paddingLeft:2,
              fontSize:10, color:T.muted, cursor:"pointer", userSelect:"none",
            }}>
              <input type="checkbox" checked={includeNotesInSearch} onChange={() => setIncludeNotesInSearch(prev => !prev)} style={{ accentColor:T.blue, cursor:"pointer", width:12, height:12, margin:0 }} />
              Include notes
            </label>
          </div>

          <span style={{ width:1, height:20, background:T.border, alignSelf:"center", margin:"0 2px" }} />

          {/* 2) Severity segmented multi-select (one bordered unit, no gaps) */}
          <div style={{ display:"inline-flex", border:`1px solid ${T.border}`, borderRadius:8, overflow:"hidden" }}>
            {SEVERITY_SEGMENTS.map((s, i) => {
              const active = severityFilter.includes(s.value);
              return (
                <button key={s.value} onClick={() => toggleSeverity(s.value)} title={s.label} style={{
                  padding:"6px 10px", border:"none",
                  borderLeft: i === 0 ? "none" : `1px solid ${T.border}`,
                  // Active = a tint of the segment's OWN severity color; a stronger
                  // inset ring stands in for the border (segments share one frame).
                  background: active ? hexA(s.color, 0.22) : "rgba(255,255,255,.03)",
                  boxShadow: active ? `inset 0 0 0 1px ${hexA(s.color, 0.6)}` : "none",
                  color:T.text, cursor:"pointer", fontFamily:"inherit", fontSize:12, transition:".15s",
                  display:"inline-flex", alignItems:"center", gap:6, whiteSpace:"nowrap",
                }}>
                  <span style={{ display:"inline-block", width:7, height:7, borderRadius:"50%", background:s.color, opacity: active ? 1 : 0.5 }} />
                  {s.label}
                </button>
              );
            })}
          </div>

          {/* 3) Status multi-select dropdown */}
          <div style={{ position:"relative", display:"inline-block" }}>
            <button onClick={() => setStatusMenuOpen(o => !o)} style={barToggle(statusFilter.length > 0)}>
              Status
              {statusFilter.length > 0 && <span style={{
                padding:"0 6px", borderRadius:999, background:"rgba(68,166,255,.3)",
                color:T.text, fontSize:10, fontWeight:700,
              }}>{statusFilter.length}</span>}
              <span style={{ fontSize:9, opacity:.7 }}>▼</span>
            </button>
            {statusMenuOpen && (
              <>
                {/* Click-away scrim */}
                <div onClick={() => setStatusMenuOpen(false)} style={{ position:"fixed", inset:0, zIndex:60 }} />
                <div style={{
                  position:"absolute", top:"calc(100% + 4px)", left:0, zIndex:61, minWidth:200,
                  background:T.panel, border:`1px solid ${T.border}`, borderRadius:10,
                  boxShadow:"0 12px 32px rgba(0,0,0,.4)", padding:6,
                }}>
                  {STATUS_OPTIONS.map(o => {
                    const checked = statusFilter.includes(o.value);
                    return (
                      <label key={o.value} style={{
                        display:"flex", alignItems:"center", gap:8, padding:"6px 8px", borderRadius:6,
                        cursor:"pointer", fontSize:12, color:T.text,
                      }}>
                        <input type="checkbox" checked={checked} onChange={() => toggleStatus(o.value)} style={{ accentColor:T.blue, cursor:"pointer" }} />
                        {o.label}
                      </label>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          {/* 4) Category dropdown — custom single-select (NOT native <select>): native
              <option> background/color is ignored by Windows, rendering hazy/unreadable.
              Mirrors the Status dropdown construction so colors are fully CSS-controlled.
              Still flows through PRODUCT_CAT_MAP via catFilter (one category, or null = all). */}
          <div style={{ position:"relative", display:"inline-block" }}>
            <button onClick={() => setCatMenuOpen(o => !o)} style={barToggle(!!catFilter)}>
              {catFilter || "Product"}
              <span style={{ fontSize:9, opacity:.7 }}>▼</span>
            </button>
            {catMenuOpen && (
              <>
                {/* Click-away scrim */}
                <div onClick={() => setCatMenuOpen(false)} style={{ position:"fixed", inset:0, zIndex:60 }} />
                <div style={{
                  position:"absolute", top:"calc(100% + 4px)", left:0, zIndex:61, minWidth:200,
                  background:T.panel, border:`1px solid ${T.border}`, borderRadius:10,
                  boxShadow:"0 12px 32px rgba(0,0,0,.4)", padding:6, maxHeight:320, overflowY:"auto",
                }}>
                  {[{ label:"All Products", value:null }, ...Array.from(new Set(recs.map(r => r.cat))).sort().map(c => ({ label:c, value:c }))].map(opt => {
                    const selected = (catFilter || null) === opt.value;
                    const pick = () => { setCatFilter(opt.value); onCatFilterChange && onCatFilterChange(opt.value); setCatMenuOpen(false); };
                    return (
                      <div
                        key={opt.value || "__all__"}
                        onClick={pick}
                        onMouseEnter={e => { if (!selected) e.currentTarget.style.background = "rgba(255,255,255,0.05)"; }}
                        onMouseLeave={e => { if (!selected) e.currentTarget.style.background = "transparent"; }}
                        style={{
                          display:"flex", alignItems:"center", justifyContent:"space-between", gap:8,
                          padding:"6px 8px", borderRadius:6, cursor:"pointer", fontSize:12, color:T.text,
                          background: selected ? "rgba(68,166,255,0.15)" : "transparent", whiteSpace:"nowrap",
                        }}
                      >
                        <span>{opt.label}</span>
                        {selected && <span style={{ color:T.blue, fontSize:12 }}>✓</span>}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          {/* Rating filters — blank/missing values are intentionally surfaced as Not rated. */}
          {[
            ["Complexity", [...new Set(["Low", "Medium", "High", "Not rated", ...recs.map(r => ratingValue(complexityForDisplay(r)))])], difficultyFilter, setDifficultyFilter],
            ["User disruption", [...new Set(["None", "Low", "Medium", "High", "Not rated", ...recs.map(r => ratingValue(r.user_impact))])], impactFilter, setImpactFilter],
          ].map(([label, options, selected, setSelected]) => (
            <div key={label} style={{ position:"relative" }}>
              <button type="button" aria-expanded={ratingMenu === label} onClick={() => setRatingMenu(open => open === label ? null : label)} style={barToggle(selected.length > 0)}>{label}{selected.length ? `: ${selected.length}` : ""} <span style={{ fontSize:9, opacity:.7 }}>▼</span></button>
              {ratingMenu === label && <div style={{ position:"absolute", zIndex:62, top:"calc(100% + 4px)", left:0, minWidth:170, padding:8, background:T.panel, border:`1px solid ${T.border}`, borderRadius:10, boxShadow:"0 12px 32px rgba(0,0,0,.4)" }}>
                {options.map(value => <label key={value} style={{ display:"flex", alignItems:"center", gap:7, padding:"4px", fontSize:12, cursor:"pointer", whiteSpace:"nowrap" }}>
                  <input type="checkbox" checked={selected.includes(value)} onChange={() => setSelected(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value])} />{value}
                </label>)}
              </div>}
            </div>
          ))}
          {/* 5) Toggles */}
          <button onClick={() => setHasNotesFilter(prev => !prev)} style={barToggle(hasNotesFilter)}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill={hasNotesFilter ? T.text : "none"} stroke={hasNotesFilter ? T.text : "currentColor"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
            Has Notes
          </button>
          {(() => {
            // Three states for Regressed (inline so barToggle / other toggles are unaffected):
            //  1) no regressions      → neutral (like the other toggles)
            //  2) regressions, OFF    → resting alert: red OUTLINE, transparent fill
            //  3) filter ON           → engaged: red FILL, red outline (clearly stronger than 2)
            const hasReg = regressedCount > 0;
            const base = {
              padding:"6px 10px", borderRadius:8, cursor:"pointer", fontFamily:"inherit",
              fontSize:12, transition:".15s", display:"inline-flex", alignItems:"center", gap:6, whiteSpace:"nowrap",
            };
            let style, redInk;
            if (regressedFilter && hasReg) {
              // Active / engaged — filled.
              style = { ...base, border:"1px solid rgba(239,68,68,.55)", background:"rgba(239,68,68,.22)", color:"#FECACA" };
              redInk = true;
            } else if (hasReg) {
              // Resting alert — outline only.
              style = { ...base, border:"1px solid rgba(239,68,68,.55)", background:"transparent", color:"#FECACA" };
              redInk = true;
            } else {
              // Neutral (no regressions). Mirrors barToggle's inactive look.
              style = { ...base, border:`1px solid ${T.border}`, background:"rgba(255,255,255,.04)", color:T.text };
              redInk = false;
            }
            return (
              <button onClick={() => { const nv = !regressedFilter; setRegressedFilter(nv); onRegressedFilterChange && onRegressedFilterChange(nv); }} style={style}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={redInk ? "#EF4444" : "currentColor"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 18 13.5 8.5 8.5 13.5 1 6"/><polyline points="17 18 23 18 23 12"/></svg>
                Regressed
                {regressedCount > 0 && <span style={{
                  padding:"0 6px", borderRadius:999,
                  background: regressedFilter ? "rgba(239,68,68,.25)" : "rgba(239,68,68,.18)",
                  color:"#FECACA", fontSize:10, fontWeight:700,
                }}>{regressedCount}</span>}
              </button>
            );
          })()}
          <button onClick={() => setPastRevisitFilter(prev => !prev)} style={barToggle(pastRevisitFilter, "rgba(225,77,87,")}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={pastRevisitFilter ? T.red : "currentColor"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            Past revisit
            {pastRevisitCount > 0 && <span style={{
              padding:"0 6px", borderRadius:999,
              background: pastRevisitFilter ? "rgba(225,77,87,.25)" : "rgba(225,77,87,.18)",
              color:T.red, fontSize:10, fontWeight:700,
            }}>{pastRevisitCount}</span>}
          </button>
          {/* Hidden-classes dropdown — replaced the Include Satisfied chip when
              the 2026-07-28 decision added Not Assessed as a second
              hidden-by-default class. Checked = hidden from the list; the
              closed label counts currently-hidden classes. Same construction
              as the Status dropdown (scrim + panel). */}
          {(() => {
            const satisfiedTotal = recs.filter(r => r.status === "implemented").length;
            const notAssessedTotal = recs.filter(r => r.verdict === "not_assessed").length;
            const hiddenN = (!showSatisfied ? 1 : 0) + (!showNotAssessed ? 1 : 0);
            const hideRow = (label, count, hidden, onToggle) => (
              <label key={label} style={{
                display:"flex", alignItems:"center", gap:8, padding:"6px 8px", borderRadius:6,
                cursor:"pointer", fontSize:12, color:T.text, whiteSpace:"nowrap",
              }}>
                <input type="checkbox" checked={hidden} onChange={onToggle} style={{ accentColor:T.blue, cursor:"pointer" }} />
                {label} <span style={{ color:T.muted }}>({count})</span>
              </label>
            );
            return (
              <div style={{ position:"relative", display:"inline-block" }}>
                <button onClick={() => setHiddenMenuOpen(o => !o)} style={barToggle(false)}>
                  Hidden: {hiddenN}
                  <span style={{ fontSize:9, opacity:.7 }}>▼</span>
                </button>
                {hiddenMenuOpen && (
                  <>
                    {/* Click-away scrim */}
                    <div onClick={() => setHiddenMenuOpen(false)} style={{ position:"fixed", inset:0, zIndex:60 }} />
                    <div style={{
                      position:"absolute", top:"calc(100% + 4px)", left:0, zIndex:61, minWidth:190,
                      background:T.panel, border:`1px solid ${T.border}`, borderRadius:10,
                      boxShadow:"0 12px 32px rgba(0,0,0,.4)", padding:6,
                    }}>
                      <div style={{ padding:"4px 8px 6px", fontSize:10, color:T.muted, textTransform:"uppercase", letterSpacing:".05em", fontWeight:600 }}>Hidden from list</div>
                      {hideRow("Satisfied", satisfiedTotal, !showSatisfied, () => setShowSatisfied(prev => !prev))}
                      {hideRow("Not Assessed", notAssessedTotal, !showNotAssessed, () => {
                        setShowNotAssessed(prev => {
                          // Re-hiding the class also drops its status-filter
                          // selection so the user is never filtering for rows
                          // that can't render.
                          if (prev) setStatusFilter(sf => sf.filter(v => v !== "not_assessed"));
                          return !prev;
                        });
                      })}
                    </div>
                  </>
                )}
              </div>
            );
          })()}

          {/* Spacer pushes Session Focus to the right (Clear all now lives in the chips row) */}
          <span style={{ flex:1 }} />

          {/* Review complete — the Patriot-only session-end action,
              paired with Session Focus (the tool used DURING the
              session). Hidden in bulk mode like its neighbour; the
              component itself hides from customers and in presentation
              mode. */}
          {!bulkMode && reviewCompleteControl}

          {/* 6) Session Focus (Clear all moved to the active-filter chips row).
              Hidden in bulk mode — the panel itself is suppressed there, so the
              button would toggle an invisible panel. */}
          {!bulkMode && <SessionFocusButton
            count={queueCount}
            max={MAX_QUEUE}
            isOpen={sessionFocusPanelState === "expanded"}
            hasError={!!sessionFocusError}
            onToggle={() => onSessionFocusPanelStateChange(sessionFocusPanelState === "expanded" ? "closed" : "expanded")}
          />}
        </div>
      </div>
      {/* Count + active-filter chips (each chip × clears just that filter).
          Rendered in bulk mode too — lingering deep-link filters constrain the
          picker, so they must be visible and clearable while selecting. */}
      {(() => {
        const chipStyle = {
          display:"inline-flex", alignItems:"center", gap:6, padding:"3px 8px", borderRadius:999,
          border:"1px solid rgba(68,166,255,.4)", background:"rgba(68,166,255,.12)", color:T.text, fontSize:11,
        };
        const xStyle = { cursor:"pointer", opacity:.7, fontSize:13, lineHeight:1 };
        const chips = [];
        severityFilter.forEach(v => chips.push(
          <span key={`sev-${v}`} style={chipStyle}>{SEVERITY_LABEL[v] || v}
            <span style={xStyle} onClick={() => toggleSeverity(v)}>×</span></span>
        ));
        statusFilter.forEach(v => chips.push(
          <span key={`st-${v}`} style={chipStyle}>{STATUS_LABEL[v] || v}
            <span style={xStyle} onClick={() => toggleStatus(v)}>×</span></span>
        ));
        difficultyFilter.forEach(v => chips.push(
          <span key={`difficulty-${v}`} style={chipStyle}>Complexity: {v}
            <button aria-label={`Remove Complexity: ${v}`} onClick={() => setDifficultyFilter(prev => prev.filter(x => x !== v))} style={{ ...xStyle, border:"none", background:"none", color:"inherit" }}>×</button></span>
        ));
        impactFilter.forEach(v => chips.push(
          <span key={`impact-${v}`} style={chipStyle}>User disruption: {v}
            <button aria-label={`Remove User disruption: ${v}`} onClick={() => setImpactFilter(prev => prev.filter(x => x !== v))} style={{ ...xStyle, border:"none", background:"none", color:"inherit" }}>×</button></span>
        ));
        if (catFilter) chips.push(
          <span key="cat" style={chipStyle}>{catFilter}
            <span style={xStyle} onClick={() => { setCatFilter(null); onCatFilterChange && onCatFilterChange(null); }}>×</span></span>
        );
        if (categoryFilter) chips.push(
          <span key="category" style={chipStyle}>{categoryFilter}
            <span style={xStyle} onClick={() => { setCategoryFilter(null); onCategoryFilterChange && onCategoryFilterChange(null); }}>×</span></span>
        );
        if (overrideFilter) chips.push(
          <span key="ov" style={chipStyle}>{OVERRIDE_CHIP_LABEL[overrideFilter] || overrideFilter}
            <span style={xStyle} onClick={() => { setOverrideFilter(null); onOverrideFilterChange && onOverrideFilterChange(null); }}>×</span></span>
        );
        if (exactSelection) chips.push(
          <span key="remediated" style={chipStyle}>{exactSelection.label || "Remediated since prior scan"}
            <span style={xStyle} onClick={() => onExactSelectionChange && onExactSelectionChange(null)}>×</span></span>
        );
        if (search.trim()) chips.push(
          <span key="q" style={chipStyle}>Search: “{search.trim()}”
            <span style={xStyle} onClick={() => setSearch("")}>×</span></span>
        );
        if (hasNotesFilter) chips.push(
          <span key="notes" style={chipStyle}>Has Notes
            <span style={xStyle} onClick={() => setHasNotesFilter(false)}>×</span></span>
        );
        if (regressedFilter) chips.push(
          <span key="regr" style={chipStyle}>Regressed
            <span style={xStyle} onClick={() => { setRegressedFilter(false); onRegressedFilterChange && onRegressedFilterChange(false); }}>×</span></span>
        );
        if (quickWinsFilter) chips.push(
          <span key="qw" style={chipStyle}>Quick Wins
            <span style={xStyle} onClick={() => setQuickWinsFilter(false)}>×</span></span>
        );
        if (needsActionFilter) chips.push(
          <span key="na" style={chipStyle}>Needs Action
            <span style={xStyle} onClick={() => setNeedsActionFilter(false)}>×</span></span>
        );
        if (showSatisfied) chips.push(
          <span key="sat" style={chipStyle}>Showing Satisfied
            <span style={xStyle} onClick={() => setShowSatisfied(false)}>×</span></span>
        );
        if (showNotAssessed) chips.push(
          <span key="nass" style={chipStyle}>Showing Not Assessed
            <span style={xStyle} onClick={() => {
              setShowNotAssessed(false);
              // Re-hiding also drops the class's status-filter selection.
              setStatusFilter(sf => sf.filter(v => v !== "not_assessed"));
            }}>×</span></span>
        );
        if (pastRevisitFilter) chips.push(
          <span key="pr" style={chipStyle}>Past revisit
            <span style={xStyle} onClick={() => setPastRevisitFilter(false)}>×</span></span>
        );
        return (
          <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap", marginBottom:12, fontSize:13, color:T.muted }}>
            <span>
              Showing <strong style={{ color:T.text }}>{list.length}</strong> of {recs.length} settings
              {(() => {
                // Coverage cue: when the Not Assessed class is hidden, say how
                // many rows the list is silently excluding.
                const n = recs.filter(r => r.verdict === "not_assessed").length;
                return n > 0 && !showNotAssessed
                  ? <span style={{ color:T.muted }}> · {n} not assessed</span>
                  : null;
              })()}
            </span>
            {chips.length > 0 && <span style={{ width:1, height:14, background:T.border }} />}
            {chips}
            {anyFilterActive && (
              <button onClick={clearAllFilters} style={{
                background:"none", border:"none", color:T.blue, cursor:"pointer",
                fontFamily:"inherit", fontSize:12, padding:"0 2px",
              }}>Clear all</button>
            )}
            <span style={{ marginLeft:"auto", display:"flex", alignItems:"center", gap:6 }}>
              <div style={{ position:"relative" }}>
                <button
                  type="button"
                  aria-haspopup="dialog"
                  aria-expanded={columnsMenuOpen}
                  aria-label={`Columns, ${visibleColumnIds.length} of ${RECS_COLUMN_IDS.length} visible`}
                  onClick={() => setColumnsMenuOpen(open => !open)}
                  style={barToggle(columnsMenuOpen)}
                >Columns {visibleColumnIds.length}/{RECS_COLUMN_IDS.length}<span style={{ fontSize:9, opacity:.7 }}>▼</span></button>
                {columnsMenuOpen && <>
                  <div onClick={() => setColumnsMenuOpen(false)} style={{ position:"fixed", inset:0, zIndex:60 }} />
                  <div role="dialog" aria-label="Visible columns" style={{
                    position:"absolute", top:"calc(100% + 4px)", right:0, zIndex:61, minWidth:270,
                    maxHeight:380, overflowY:"auto", padding:8, background:T.panel,
                    border:`1px solid ${T.border}`, borderRadius:10, boxShadow:"0 12px 32px rgba(0,0,0,.4)",
                  }}>
                    <div style={{ padding:"3px 6px 7px", color:T.muted, fontSize:11 }}>Visible columns</div>
                    {columnPreference.order.map((id, index) => {
                      const definition = RECS_COLUMN_DEFINITIONS.find(column => column.id === id);
                      const checked = columnPreference.visible.includes(id);
                      const label = id === "dimension"
                        ? { product:"Product", category:"Security Domain", license:"License" }[colDim]
                        : definition.label;
                      return <div key={id} style={{ display:"flex", alignItems:"center", gap:4, minHeight:34, padding:"2px 4px", borderBottom:`1px solid ${T.border}` }}>
                        <label style={{ display:"flex", alignItems:"center", gap:8, flex:1, minWidth:0, cursor:definition.required ? "default" : "pointer", fontSize:12, color:T.text }}>
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={definition.required}
                            onChange={event => setColumnVisible(id, event.target.checked)}
                            aria-label={`${label}${definition.required ? ", required" : ""}`}
                            style={{ accentColor:T.blue, cursor:definition.required ? "default" : "pointer" }}
                          />
                          <span>{label}</span>
                          {definition.required && <span style={{ color:T.muted, fontSize:10 }}>Required</span>}
                        </label>
                        {!definition.required && <span style={{ display:"inline-flex", gap:2 }}>
                          <button type="button" aria-label={`Move ${label} left`} disabled={index === 0} onClick={() => moveColumn(id, -1)} style={{ ...columnMoveButtonStyle, opacity:index === 0 ? .35 : 1 }}>‹</button>
                          <button type="button" aria-label={`Move ${label} right`} disabled={index === columnPreference.order.length - 1} onClick={() => moveColumn(id, 1)} style={{ ...columnMoveButtonStyle, opacity:index === columnPreference.order.length - 1 ? .35 : 1 }}>›</button>
                        </span>}
                      </div>;
                    })}
                    <button type="button" onClick={resetColumns} style={{ margin:"8px 4px 2px", border:0, background:"none", color:T.blue, cursor:"pointer", font:"inherit", fontSize:12 }}>Reset to default</button>
                  </div>
                </>}
              </div>
              <span style={{ fontSize:11, color:T.muted }}>Display:</span>
              <ViewToggle size="sm" options={[{value:"product",label:"Product"},{value:"category",label:"Security Domain"},{value:"license",label:"License"}]} value={colDim} onChange={setColDim} />
            </span>
          </div>
        );
      })()}
      {sessionFocusBanner && !bulkMode && (
        <div style={{
          padding:"10px 14px", marginBottom:12, borderRadius:10, fontSize:13,
          display:"flex", alignItems:"center", gap:10,
          background: sessionFocusBanner.kind === "queue_full" ? "rgba(243,181,63,.12)" : "rgba(225,77,87,.12)",
          border:`1px solid ${sessionFocusBanner.kind === "queue_full" ? "rgba(243,181,63,.35)" : "rgba(225,77,87,.35)"}`,
          color: sessionFocusBanner.kind === "queue_full" ? T.yellow : T.red,
        }}>
          <span style={{ flex:1 }}>{sessionFocusBanner.text}</span>
          <button onClick={onDismissSessionFocusBanner} aria-label="Dismiss" style={{
            background:"none", border:"none", color:"inherit", cursor:"pointer", fontSize:14, fontWeight:700, fontFamily:"inherit",
          }}>×</button>
        </div>
      )}
      {reviewBanner && !bulkMode && (
        <div style={{
          padding:"10px 14px", marginBottom:12, borderRadius:10, fontSize:13,
          display:"flex", alignItems:"center", gap:10,
          background:"rgba(225,77,87,.12)", border:`1px solid rgba(225,77,87,.35)`, color: T.red,
        }}>
          <span style={{ flex:1 }}>{reviewBanner.text}</span>
          {onDismissReviewBanner && (
            <button onClick={onDismissReviewBanner} aria-label="Dismiss" style={{
              background:"none", border:"none", color:"inherit", cursor:"pointer", fontSize:14, fontWeight:700, fontFamily:"inherit",
            }}>×</button>
          )}
        </div>
      )}
      {sessionFocusPanelState === "expanded" && !bulkMode && (
        <div style={{ marginBottom:14 }}>
          <SessionFocusCard
            items={sessionFocus || []}
            recs={recs}
            settingDetails={settingDetails}
            notesBySettingId={notesBySettingId}
            reviewsBySettingId={reviewsBySettingId}
            onMarkReviewed={onMarkReviewed}
            isPatriot={isPatriot}
            pendingReview={pendingReview}
            currentUserEmail={currentUserEmail}
            onCollapse={() => onSessionFocusPanelStateChange("closed")}
            onItemClick={onSessionFocusItemClick}
            onRemove={onRemoveSessionFocusItem}
            onUpdate={onUpdateSessionFocusItem}
            onReorder={onReorderSessionFocus}
            onNavQueueTab={onNavQueue}
          />
        </div>
      )}
      <Card style={{ minWidth:0, maxWidth:"100%" }}>
        {tableScroll.overflow && <div style={{ display:"flex", justifyContent:"flex-end", gap:6, marginBottom:8 }}>
          <button type="button" aria-label="Scroll recommendation table left" disabled={!tableScroll.left} onClick={() => scrollTable(-1)} style={{ ...columnMoveButtonStyle, opacity:tableScroll.left ? 1 : .35 }}>‹</button>
          <button type="button" aria-label="Scroll recommendation table right" disabled={!tableScroll.right} onClick={() => scrollTable(1)} style={{ ...columnMoveButtonStyle, opacity:tableScroll.right ? 1 : .35 }}>›</button>
        </div>}
        <div ref={tableViewportRef} role="region" aria-label="Recommendations table, scroll horizontally for more columns" tabIndex={0} onScroll={syncTableScroll} style={{ width:"100%", maxWidth:"100%", minWidth:0, overflowX:"auto" }}>
        <table style={{ width:"100%", minWidth:tableMinWidth, borderCollapse:"collapse" }}>
          <thead><tr>
            {bulkMode && <th style={{ ...thStyle, width:28 }}></th>}
            {visibleColumnIds.map(id => {
              const definition = RECS_COLUMN_DEFINITIONS.find(column => column.id === id);
              const headerStyle = { ...thStyle, padding:denseTable ? "10px 6px" : thStyle.padding, ...(definition.sort ? sortableHdr : {}) };
              if (id === "number") return <th key={id} style={{ ...headerStyle, width:40 }} onClick={() => sortBy("num")}>#{sortArrow("num")}</th>;
              if (id === "status") return <th key={id} style={{ ...headerStyle, width:140 }} onClick={() => sortBy("status")}>Status{sortArrow("status")}</th>;
              if (id === "recommendation") return <th key={id} style={{ ...headerStyle, minWidth:280 }} onClick={() => sortBy("rec")}>Recommendation{sortArrow("rec")}</th>;
              if (id === "dimension") return <th key={id} style={{ ...headerStyle, width:150 }} onClick={() => sortBy("cat")}>{ {product:"Product", category:"Security Domain", license:"License"}[colDim] }{sortArrow("cat")}</th>;
              if (id === "severity") return <th key={id} style={{ ...headerStyle, width:90 }} onClick={() => sortBy("sev")}>Severity{sortArrow("sev")}</th>;
              if (id === "complexity") return <th key={id} aria-label="Complexity" style={{ ...thStyle, width:compactRatingColumns ? 50 : 110 }}>{compactRatingColumns ? <span title="Complexity" aria-label="Complexity">{ratingIcon(id)}</span> : "Complexity"}</th>;
              if (id === "userDisruption") return <th key={id} aria-label="User disruption" style={{ ...thStyle, width:compactRatingColumns ? 50 : 150 }}>{compactRatingColumns ? <span title="User disruption" aria-label="User disruption">{ratingIcon(id)}</span> : "User disruption"}</th>;
              if (id === "notes") return <th key={id} style={{ ...thStyle, width:60 }}>Notes</th>;
              if (id === "session") return <th key={id} style={{ ...thStyle, width:100 }}>SESSION ({queueCount}/{MAX_QUEUE})</th>;
              if (id === "support") return <th key={id} style={{ ...thStyle, width:70 }}>Support</th>;
              if (id === "age") return <th key={id} style={{ ...headerStyle, width:90, textAlign:"right" }} onClick={() => sortBy("age")}>Age{sortArrow("age")}</th>;
              return null;
            })}
          </tr></thead>
          <tbody>
            {list.length === 0 && (
              <tr>
                <td colSpan={colCount} style={{ padding:"48px 16px", textAlign:"center", color:T.muted, fontSize:14 }}>
                  <div style={{ marginBottom:10 }}>No settings match your filters.</div>
                  <button onClick={clearAllFilters} style={{
                    background:"none", border:"none", color:T.blue, cursor:"pointer", fontFamily:"inherit",
                    fontSize:13, fontWeight:600, textDecoration:"underline",
                  }}>Clear all</button>
                </td>
              </tr>
            )}
            {list.map(r => {
              const isExp = expanded.has(r.id);
              const det = SETTINGS_DETAIL[r.id] || "No details available.";
              const sd = settingDetails?.[r.id];
              const maxW = getMaxWeight(r.sev);
              const firstLine = (arr) => (arr?.[0]?.text || "").split("\n")[0];
              const statusCell = (() => {
                // Overrides win over Not Assessed (operator call, 2026-07-28):
                // an explicit business decision outranks the assessment gap.
                // On overridden not-assessed rows the gap rides in the tooltip.
                const naTip = r.verdict === "not_assessed"
                  ? `Not assessed this scan${(r.skip_reason || r.error_detail) ? `: ${r.skip_reason || r.error_detail}` : ""}`
                  : null;
                const tipWith = (base) => [base || "", naTip].filter(Boolean).join(" — ");
                if (sd?.override === "risk_accepted")        return { ...statusBadge("exception"),   tip: tipWith(sd.reason) };
                if (sd?.override === "customer_remediated")  return { ...statusBadge("pending"),     tip: tipWith(sd.reason) };
                if (sd?.override === "planned")              return { ...statusBadge("planned"),     tip: tipWith(sd.reason) };
                if (sd?.override === "customer_managed_solution")   return { ...statusBadge("managed_solution"),   tip: tipWith(sd.reason) };
                if (r.verdict === "not_assessed") {
                  return { ...statusBadge("not_assessed"), tip: r.skip_reason || r.error_detail || "" };
                }
                return statusBadge(r.status);
              })();
              const inQueue = queueIds && queueIds.has(r.id);
              const tdStyle = { padding:denseTable ? "10px 6px" : "10px 8px", borderBottom:`1px solid ${T.border}`, fontSize:13 };
              return [
                <tr key={r.id} style={{ background: bulkMode && bulkSel.has(r.id) ? "rgba(68,166,255,0.06)" : "transparent" }}>
                  {bulkMode && (
                    <td style={tdStyle}>
                      <input type="checkbox" checked={bulkSel.has(r.id)} onChange={() => toggleBulkItem(r.id)} style={{ width:15, height:15, accentColor:T.blue, cursor:"pointer" }} />
                    </td>
                  )}
                  {visibleColumnIds.map(id => {
                    if (id === "number") return <td key={id} style={{ ...tdStyle, color:T.muted }}>{r.id}</td>;
                    if (id === "status") return <td key={id} style={tdStyle}><span title={statusCell.tip || ""}><Badge type={statusCell.type} weight="soft">{statusCell.label}</Badge></span></td>;
                    if (id === "recommendation") return <td key={id} style={{ ...tdStyle, minWidth:280, overflowWrap:"anywhere" }}>
                      <button type="button" aria-label={`Open recommendation: ${r.name}`} ref={r.id === highlightId ? highlightRef : null} onClick={() => onOpenPanel && onOpenPanel(r, list)} style={{ display:"inline", padding:0, border:0, background:"none", color:"inherit", font:"inherit", textAlign:"left", cursor:"pointer", textDecoration:"underline dotted", textUnderlineOffset:3, overflowWrap:"anywhere" }}>
                        {r.name}{r.isNew && <> {" "}<Badge type="purple">NEW</Badge></>}
                        {r.regressed && (() => {
                          const rowOverride = settingDetails[r.id]?.override;
                          if (rowOverride === "risk_accepted" || rowOverride === "customer_managed_solution" || rowOverride === "customer_remediated") return null;
                          return <> {" "}<Badge type="red">REGRESSED</Badge></>;
                        })()}
                      </button>
                    </td>;
                    if (id === "dimension") return <td key={id} style={{ ...tdStyle, color:T.muted, maxWidth:150, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }} title={colDim === "category" ? (r.category || "") : colDim === "license" ? (r.license || "") : (r.cat || "")}>{colDim === "category" ? (r.category || "—") : colDim === "license" ? (r.license || "—") : r.cat}</td>;
                    if (id === "severity") return <td key={id} style={tdStyle}><Badge type={sevBadgeType(r.sev)}>{sevLabel(r.sev)}</Badge></td>;
                    if (id === "complexity") return <td key={id} style={tdStyle}>{ratingDisplay("complexity", complexityForDisplay(r), r.id)}</td>;
                    if (id === "userDisruption") return <td key={id} style={tdStyle}>{ratingDisplay("userDisruption", r.user_impact, r.id)}</td>;
                    if (id === "notes") return <td key={id} style={tdStyle}>{(() => {
                      const nc = realNotesCount(r.id);
                      const noteHit = matchedInNote(r);
                      const iconColor = noteHit ? T.blue : (nc > 0 ? T.text : "rgba(255,255,255,0.2)");
                      return <button onClick={() => onOpenPanel && onOpenPanel(r, list)} title={noteHit ? "Matched in a note" : undefined} aria-label={`Open notes for ${r.name}${nc ? `, ${nc} notes` : ""}`} style={{ background:"none", border:"none", cursor:"pointer", display:"flex", alignItems:"center", gap:4, padding:0 }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill={noteHit ? T.blue : (nc > 0 ? T.text : "none")} stroke={iconColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                        {nc > 0 && <span style={{ fontSize:11, fontWeight:700, color:noteHit ? T.blue : T.text }}>{nc}</span>}{noteHit && <span style={{ fontSize:9, fontWeight:700, color:T.blue, letterSpacing:".03em" }}>in note</span>}
                      </button>;
                    })()}</td>;
                    if (id === "session") return <td key={id} style={tdStyle}>{!bulkMode && (() => {
                      const reviewList = (!inQueue && reviewsBySettingId && reviewsBySettingId.get(r.id)) || [];
                      const showIndicator = reviewList.length > 0;
                      const tipLines = showIndicator ? reviewList.slice(0, 3).map(rv => `${formatReviewDate(rv.reviewed_on)} · ${rv.reviewed_by_type || "—"} · ${sessionFocusAuthorName(rv.reviewed_by_email) || rv.reviewed_by_email || "Unknown"}`).join("\n") : "";
                      const label = showIndicator ? (reviewList.length === 1 ? `🕒 ${formatReviewDate(reviewList[0].reviewed_on)}` : `🕒 ${formatReviewDate(reviewList[0].reviewed_on)} (${reviewList.length})`) : "";
                      return <div style={{ display:"flex", alignItems:"center", gap:6 }}><QueueAddBtn rec={r} inQueue={inQueue} isFull={queueCount >= MAX_QUEUE} onAdd={onAddToQueue} onRemove={onRemoveFromQueue} pending={pendingQueue.has(r.id)} />{showIndicator && <span title={tipLines} style={{ display:"inline-flex", alignItems:"center", gap:3, padding:"2px 6px", borderRadius:999, fontSize:10, color:T.muted, background:"rgba(255,255,255,.04)", border:`1px solid ${T.border}`, whiteSpace:"nowrap" }}>{label}</span>}</div>;
                    })()}</td>;
                    if (id === "support") return <td key={id} style={tdStyle}>{!bulkMode && <SupportBtn rec={r} onSingle={rec => onSupport(rec)} onBulk={enterBulk} />}</td>;
                    if (id === "age") return <td key={id} style={{ ...tdStyle, color:T.muted, textAlign:"right" }}>{ageString(r.first_identified)}</td>;
                    return null;
                  })}
                </tr>,
                isExp && (
                  <tr key={`det-${r.id}`}>
                    <td colSpan={colCount} style={{ padding:"12px 16px", background:"rgba(68,166,255,.06)", borderRadius:10 }}>
                      <div style={{ fontSize:13, color:T.muted, lineHeight:1.6 }}>{det}</div>
                    </td>
                  </tr>
                ),
              ];
            })}
          </tbody>
        </table>
        </div>
      </Card>

    </div>
  );
};


/* ══════════════════════════════════════════════
   PAGE: NEXT SESSION FOCUS (Queue)
   ══════════════════════════════════════════════ */
export const QueuePage = ({ sessionFocus, recs, settingDetails, notesBySettingId, reviewsBySettingId, onMarkReviewed, currentUserEmail, onAddToQueue, onItemClick, onRemove, onUpdate, onReorder, error, banner, onDismissBanner, sessionFocusLoading, reportLoading, reportError, noDataScanState = null, onNavProfile, pendingReview = new Set(), isPatriot = false }) => {
  const [modal, setModal] = useState(null);
  const [picks, setPicks] = useState(new Set());

  const activeItems = useMemo(
    () => (sessionFocus || []).filter(i => i.status === "Active").sort((a, b) => (a.order_index || 0) - (b.order_index || 0)),
    [sessionFocus]
  );

  const avail = useMemo(() => {
    const ids = new Set((sessionFocus || []).filter(i => i.status === "Active").map(i => i.setting_id));
    return recs.filter(r => !ids.has(r.id) && r.status === "open");
  }, [sessionFocus, recs]);

  const togglePick = (id) => setPicks(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });

  const openAdd = () => { setPicks(new Set()); setModal("add"); };
  const doAdd = async () => {
    const remaining = MAX_QUEUE - activeItems.length;
    const ids = Array.from(picks).slice(0, Math.max(0, remaining));
    for (const id of ids) {
      const r = recs.find(x => x.id === id);
      if (r) {
        // Serial to keep order_index deterministic and avoid 409 races.
        // eslint-disable-next-line no-await-in-loop
        await onAddToQueue(r);
      }
    }
    setModal(null);
  };

  if (sessionFocusLoading || reportLoading) {
    return <SkeletonNextSessionFocus />;
  }
  if (reportError === "roster_inactive") {
    return <EmptyState title={ROSTER_INACTIVE_TITLE} message={ROSTER_INACTIVE_MESSAGE} icon="🔒" />;
  }
  if (reportError === "no_data") {
    return <NoDataEmptyState scanState={noDataScanState} onGoToProfile={onNavProfile} />;
  }

  return (
    <div>
      {banner && (
        <div style={{
          padding:"10px 14px", marginBottom:12, borderRadius:10, fontSize:13,
          display:"flex", alignItems:"center", gap:10,
          background: banner.kind === "queue_full" ? "rgba(243,181,63,.12)" : "rgba(225,77,87,.12)",
          border:`1px solid ${banner.kind === "queue_full" ? "rgba(243,181,63,.35)" : "rgba(225,77,87,.35)"}`,
          color: banner.kind === "queue_full" ? T.yellow : T.red,
        }}>
          <span style={{ flex:1 }}>{banner.text}</span>
          <button onClick={onDismissBanner} aria-label="Dismiss" style={{
            background:"none", border:"none", color:"inherit", cursor:"pointer", fontSize:14, fontWeight:700, fontFamily:"inherit",
          }}>×</button>
        </div>
      )}
      <div style={{ display:"flex", gap:8, marginBottom:12, alignItems:"center" }}>
        <Btn pri onClick={openAdd}>+ Add from recommendations</Btn>
        <span style={{ flex:1 }} />
        {error && <span style={{ fontSize:12, color:T.red }}>{error}</span>}
      </div>
      <SessionFocusCard
        items={sessionFocus || []}
        recs={recs}
        settingDetails={settingDetails}
        notesBySettingId={notesBySettingId}
        reviewsBySettingId={reviewsBySettingId}
        onMarkReviewed={onMarkReviewed}
        isPatriot={isPatriot}
        pendingReview={pendingReview}
        currentUserEmail={currentUserEmail}
        onItemClick={onItemClick}
        onRemove={onRemove}
        onUpdate={onUpdate}
        onReorder={onReorder}
        fullPage={true}
      />

      <Modal show={modal === "add"} title="Add items from recommendations" onClose={() => setModal(null)} onSubmit={doAdd} submitLabel="Add selected">
        <div style={{ maxHeight:260, overflowY:"auto" }}>
          {avail.map(r => (
            <label key={r.id} style={{ display:"flex", alignItems:"center", gap:8, padding:"5px 0", borderBottom:`1px solid ${T.border}`, fontSize:12, cursor:"pointer" }}>
              <input type="checkbox" checked={picks.has(r.id)} onChange={() => togglePick(r.id)} style={{ width:15, height:15, accentColor:T.blue }} />
              <span style={{ color:T.muted, minWidth:30 }}>#{r.id}</span>
              <span style={{ flex:1 }}>{r.name}</span>
              <Badge type={sevBadgeType(r.sev)}>{sevLabel(r.sev)}</Badge>
            </label>
          ))}
          {avail.length === 0 && (
            <div style={{ padding:14, fontSize:12, color:T.muted, textAlign:"center" }}>
              No additional open recommendations available.
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};
